import React, { useState, useEffect, useRef } from "react";
import { keepSavedPricingIfUntouched, savedPricingNotice } from "./editPricingGuard";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import Grid from "@mui/material/Grid";
import Autocomplete from "@mui/material/Autocomplete";
import { FormControlLabel, MenuItem, Switch, Box, Button } from "@mui/material";
// Büyük formda her tuşta tüm formun yeniden çizilmesini önleyen tamponlu TextField (yazma gecikmesi)
import TextField from "components/BufferedTextField";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { tr } from "date-fns/locale";
import CrudService from "services/cruds-service";
import {companies} from "../../companies";
import countryOptions from "../../components/List/Countries";
import { EXTRA_CHARGE_TYPES } from "constants/extraCostOcean";
import { useNavigate, useLocation } from "react-router-dom";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import DialogContent from "@mui/material/DialogContent";
import CreateCustomer from "../../crm/customer/CreateCustomer";
import { AddRowButton, RemoveRowButton } from "components/RowActions";

const OceanForm = ({ selectedTransportType, prefill }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [exchangeRates, setExchangeRates] = useState(null);
    const [loadingRates, setLoadingRates] = useState(false);
    useEffect(() => {
        async function loadRates() {
            setLoadingRates(true);
            try {
                const res = await fetch("https://api.exchangerate-api.com/v4/latest/USD");
                const data = await res.json();
                setExchangeRates(data.rates);
            } catch (err) {
                console.error("Error fetching exchange rates:", err);
            } finally {
                setLoadingRates(false);
            }
        }
        loadRates();
    }, []);

    const convertAmount = (amount, fromCurrency, toCurrency) => {
        if (!exchangeRates) return amount; // If no rates yet, skip.
        if (!fromCurrency || !toCurrency) return amount;
        if (fromCurrency === toCurrency) return amount;
        const amountInUSD = amount / exchangeRates[fromCurrency];
        const converted = amountInUSD * exchangeRates[toCurrency];
        return converted;
    };


    const containerTypes = [
        { label: "20' DC (Dry Container)" },
        { label: "40' DC (Dry Container)" },
        { label: "40' HC (High Cube)" },
        { label: "45' HC (High Cube)" },
        { label: "53' HC (High Cube)" },
        { label: "20' RF (Reefer Container)" },
        { label: "40' RF (Reefer Container)" },
        { label: "40' HC RF (High Cube Reefer)" },
        { label: "20' OT (Open Top)" },
        { label: "40' OT (Open Top)" },
        { label: "20' FL (Flat Rack)" },
        { label: "40' FL (Flat Rack)" },
        { label: "40' Collapsible FL (Collapsible Flat Rack)" },
        { label: "20' Tank Container" },
        { label: "40' Tank Container" },
        { label: "20' Ventilated Container" },
        { label: "Modified Container" },
        { label: "Insulated Container" },
        { label: "Ventilated Coffee Container" }
    ];

    const incotermsOptions = [
        { label: "CFR" },
        { label: "CIF" },
        { label: "CIP" },
        { label: "CPT" },
        { label: "DAP" },
        { label: "DAT" },
        { label: "DPU" },
        { label: "DDP" },
        { label: "EXW" },
        { label: "FAS" },
        { label: "FCA" },
        { label: "FOB" },
        { label: "FOT" },
        { label: "FOR" },
    ];
    const chargeTypeOptions = [
        { label: "Pre Carriage" },
        { label: "Ocean Freight Fee" },
    ];

    const DEFAULT_LINE_OPTIONS = [ { id: 1, name: "HAPAG LLOYD" }, { id: 2, name: "CMA CGM" }, { id: 3, name: "MAERSK" }, { id: 4, name: "ONE LINE" }, { id: 5, name: "MSC" }, { id: 6, name: "YANG MING" }, { id: 7, name: "OOCL" }, { id: 8, name: "EVERGREEN" }, { id: 9, name: "SEALEAD" }, { id: 10, name: "WAN HAI" }, { id: 11, name: "MLH" }, { id: 12, name: "ECULINE" }, { id: 13, name: "VANGUARD" }, { id: 14, name: "SHIPCO" }, { id: 15, name: "CATONI" }, { id: 16, name: "ZIM" }, { id: 17, name: "ARKAS LINE" }, { id: 18, name: "AKKON" }, { id: 19, name: "MEDKON" }, { id: 20, name: "TURKON" }, { id: 21, name: "ADMIRAL" }, ];

    const statusOptions = [
        { value: "pending",  label: "Pending",     color: "#FFB300" },
        { value: "sent",     label: "Sent",        color: "#1E88E5" },
        { value: "secured",  label: "Secured",     color: "#43A047" },
        { value: "lost",     label: "Lost",        color: "#E53935" },
        { value: "closed",   label: "Closed",      color: "#546E7A" },
    ];

    const Dot = ({ color }) => (
        <span
            style={{
                display: "inline-block",
                width: 10,
                height: 10,
                borderRadius: "50%",
                backgroundColor: color || "#999",
                marginRight: 8,
                verticalAlign: "middle",
                flexShrink: 0,
            }}
        />
    );

    const serviceOptions = [
        { label: "Port to Port" },
        { label: "Port to Door" },
        { label: "Door to Port" },
        { label: "Door to Door" },
    ];

    const [inputValue, setInputValue] = useState("");
    const [open, setOpen] = useState(false);
    const [serviceProviderOptions, setServiceProviderOptions] = useState([]);
    const [customerOptions, setCustomerOptions] = useState([]);

    const [partyOptions, setPartyOptions] = useState([]);

    const [customerModalOpen, setCustomerModalOpen] = useState(false);
    const [customerModalTarget, setCustomerModalTarget] = useState(null);

    const NEW_PARTY_OPTION = {
        id: "new-party",
        name: "+ New Account",
        type: "new",
    };

    useEffect(() => {
        const fetchOptions = async () => {
            try {
                const [customersRes, agentsRes] = await Promise.all([
                    CrudService.getCustomers(),
                    CrudService.getGlobalAgents(),
                ]);

                const customers = (customersRes.data || []).map((c) => ({
                    id: c.id,
                    name: c.attributes?.name || "Unnamed Customer",
                    type: "customer",
                }));

                const agents = (agentsRes.data || []).map((a) => ({
                    id: a.id,
                    name: a.attributes?.name || a.name || "Unnamed Global Agent",
                    type: "agent",
                }));

                const merged = [...customers, ...agents].sort((x, y) =>
                    (x.name || "").localeCompare(y.name || "")
                );

                setCustomerOptions(customers);  // ✅ EKLE
                setPartyOptions(merged);
            } catch (err) {
                console.error("Options alınırken hata oluştu:", err);
            } finally {
                setCustomersLoaded(true);
            }
        };

        fetchOptions();
    }, []);


// companies (string[]) + partyOptions (object[]) => string[]
    const shipperConsigneeOptions = React.useMemo(() => {
        const companyItems = (companies || [])
            .map((name) => String(name || "").trim())
            .filter(Boolean)
            .map((name) => ({
                id: `company-${name.toLowerCase()}`,
                name,
                type: "company",
            }));

        const partyItems = (partyOptions || [])
            .filter((p) => p?.name)
            .map((p) => ({
                id: `${p.type}-${p.id}`,
                name: String(p.name).trim(),
                type: p.type, // "customer" | "agent"
            }));

        // unique by (type + name) (aynı isim farklı tipte olabilir)
        const map = new Map();
        [...companyItems, ...partyItems].forEach((item) => {
            const key = `${item.type}::${item.name.toLowerCase()}`;
            if (!map.has(key)) map.set(key, item);
        });

        return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
    }, [partyOptions, companies]);

    const findOptionByName = (name) => {
        const target = String(name || "").trim().toLowerCase();

        if (!target) return null;

        return (
            shipperConsigneeOptions.find(
                (o) => String(o.name || "").trim().toLowerCase() === target
            ) || {
                id: `manual-${target}`,
                name,
                type: "manual",
            }
        );
    };

    const handleCustomerCreatedFromModal = (createdCustomer) => {
        const attr = createdCustomer?.attributes || createdCustomer;

        const newOption = {
            id: createdCustomer?.id || attr?._id || Date.now(),
            name: attr?.name || "Unnamed Customer",
            type: "customer",
        };

        setCustomerOptions((prev) => {
            const exists = prev.some(
                (x) => x.name?.toLowerCase() === newOption.name.toLowerCase()
            );

            return exists ? prev : [...prev, newOption];
        });

        setPartyOptions((prev) => {
            const exists = prev.some(
                (x) =>
                    x.type === "customer" &&
                    x.name?.toLowerCase() === newOption.name.toLowerCase()
            );

            return exists ? prev : [...prev, newOption];
        });

        if (customerModalTarget === "customer") {
            setSelectedCustomer(newOption);
            setInputValue(newOption.name);
        }

        if (customerModalTarget === "shipper") {
            setShipper(newOption.name);
        }

        if (customerModalTarget === "consignee") {
            setConsignee(newOption.name);
        }

        setCustomerModalOpen(false);
        setCustomerModalTarget(null);
    };


    const [salesRepOptions, setSalesRepOptions] = useState([]);

    useEffect(() => {
        const fetchSalesReps = async () => {
            try {
                const response = await CrudService.getUsers();
                const users = response.data;

                const formattedUsers = users.map(user => ({
                    id: user.id,
                    label: user.attributes.name
                }));

                setSalesRepOptions(formattedUsers);
            } catch (error) {
                console.error("Satış temsilcileri alınamadı:", error);
            } finally {
                setSalesRepsLoaded(true);
            }
        };

        fetchSalesReps();
    }, []);


    const [loading, setLoading] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const searchParams = new URLSearchParams(location.search);
    const quotingToolId = searchParams.get("id");
    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const submittingRef = useRef(false); // çift tıklamada mükerrer kayıt oluşmasın
    const [customersLoaded, setCustomersLoaded] = useState(false);
    const [salesRepsLoaded, setSalesRepsLoaded] = useState(false);
    const [editDataLoaded, setEditDataLoaded] = useState(false);
    const editFetchStartedRef = useRef(false);
    const savedRecordRef = useRef(null); // düzenlenen kaydın DB'deki hali (fiyat koruması için)
    // Kayıttan sonraki yönlendirme: kullanıcı bu sürede sayfadan çıkarsa onu geri tabloya atmasın
    const redirectTimerRef = useRef(null);
    useEffect(() => () => clearTimeout(redirectTimerRef.current), []);
    const successMessageRef = useRef(null);

    // Düzenlenecek kaydı müşteri/temsilci listeleri yüklendikten sonra yalnızca bir kez çek.
    // Liste sonradan değişirse (ör. modal ile yeni müşteri eklenince) kullanıcının
    // kaydedilmemiş değişiklikleri DB verisiyle ezilmesin.
    useEffect(() => {
        if (!quotingToolId) return;
        setIsEditing(true);
        if (!customersLoaded || !salesRepsLoaded) return;
        if (editFetchStartedRef.current) return;
        editFetchStartedRef.current = true;
        fetchQuotingToolDetails(quotingToolId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [quotingToolId, customersLoaded, salesRepsLoaded]);


    const fetchQuotingToolDetails = async (id) => {
        setLoading(true);
        try {
            const response = await CrudService.getQuotingToolById(id);
            const quotingToolData = response.data.attributes;

            // Müşteri ve Temsilci Seçenekleri
            const matchedCustomer = customerOptions.find(
                (opt) =>
                    opt.name?.trim().toLowerCase() ===
                    quotingToolData.customer?.trim().toLowerCase()
            );
            setSelectedCustomer(matchedCustomer || (quotingToolData.customer ? { id: null, name: quotingToolData.customer, type: "customer" } : null)); // listede yoksa kayıttaki adı koru
            setSelectedContainer (
                containerTypes .find((opt) => opt.label === quotingToolData.container) || null,
            );
            setSalesRep(
                Array.isArray(quotingToolData.salesRep)
                    ? salesRepOptions.filter(repOption =>
                        quotingToolData.salesRep.includes(repOption.id)
                    )
                    : []
            );
            setQuoteType(quotingToolData.quoteType ?? "");
            setOceanTransportType(quotingToolData.oceanTransportType ?? "");
            setShipper(quotingToolData.shipper ?? "");
            setHsCode(quotingToolData.hsCode ?? "");
            setConsignee(quotingToolData.consignee ?? "");
            setGrossWeight(quotingToolData.grossWeight ?? "");
            setVolume(quotingToolData.volume ?? "");
            setQuantity(quotingToolData.quantity ?? "");
            setCargoDescription(quotingToolData.cargoDescription ?? "");
            setPol(quotingToolData.pol ?? "");
            setPod(quotingToolData.pod ?? "");
            setFinalDestination(quotingToolData.finalDestination ?? "");
            setQuoteNo(quotingToolData.quoteNo ?? "");
            setTransitTime(quotingToolData.transitTime ?? "");
            setOriginFreeTime(quotingToolData.originFreeTime ?? "");
            setDestinationFreeTime(quotingToolData.destinationFreeTime ?? "");
            setSelectedIncoterm(quotingToolData.incoterm ?? "");
            setSelectedStatus(quotingToolData.status ?? "");
            const parseSavedServiceAddress = (structuredAddress, stringAddress) => {
                if (
                    structuredAddress &&
                    typeof structuredAddress === "object" &&
                    !Array.isArray(structuredAddress)
                ) {
                    return {
                        ...emptyServiceAddress,
                        type: "door",
                        country: String(structuredAddress.country || "").trim(),
                        city: String(structuredAddress.city || "").trim(),
                        additionalInfo: String(
                            structuredAddress.additionalInfo ||
                            structuredAddress.fullAddress ||
                            ""
                        ).trim(),
                    };
                }

                if (typeof stringAddress === "string" && stringAddress.trim()) {
                    const parts = stringAddress
                        .split("/")
                        .map((part) => part.trim());

                    const [country = "", city = "", ...addressParts] = parts;

                    return {
                        ...emptyServiceAddress,
                        type: "door",
                        country,
                        city,
                        additionalInfo: addressParts.join(" / "),
                    };
                }

                return { ...emptyServiceAddress };
            };

            setSelectedService(
                String(quotingToolData.service ?? "")
                    .trim()
                    .replace(/\s+/g, " ")
            );

            setPickupAddress(
                parseSavedServiceAddress(
                    quotingToolData.pickupAddress1 || quotingToolData.pickupAddressDetail,
                    quotingToolData.pickupAddress
                )
            );

            setDeliveryAddress(
                parseSavedServiceAddress(
                    quotingToolData.deliveryAddress2 || quotingToolData.deliveryAddressDetail,
                    quotingToolData.deliveryAddress
                )
            );

            setPickupService({
                ...emptyServiceCharge,
                ...(quotingToolData.pickupService || {}),
                serviceProvider: quotingToolData.pickupService?.serviceProvider ?? "",
                cost: quotingToolData.pickupService?.cost ?? "",
                costCurrency: quotingToolData.pickupService?.costCurrency || "EUR",
                sellingIncluded: Boolean(quotingToolData.pickupService?.sellingIncluded),
                selling: quotingToolData.pickupService?.sellingIncluded
                    ? ""
                    : (quotingToolData.pickupService?.selling ?? ""),
                sellingCurrency: quotingToolData.pickupService?.sellingCurrency || "EUR",
            });

            setDeliveryService({
                ...emptyServiceCharge,
                ...(quotingToolData.deliveryService || {}),
                serviceProvider: quotingToolData.deliveryService?.serviceProvider ?? "",
                cost: quotingToolData.deliveryService?.cost ?? "",
                costCurrency: quotingToolData.deliveryService?.costCurrency || "EUR",
                sellingIncluded: Boolean(quotingToolData.deliveryService?.sellingIncluded),
                selling: quotingToolData.deliveryService?.sellingIncluded
                    ? ""
                    : (quotingToolData.deliveryService?.selling ?? ""),
                sellingCurrency: quotingToolData.deliveryService?.sellingCurrency || "EUR",
            });

            setEstimatedCloseDate(
                quotingToolData.estimatedCloseDate
                    ? new Date(quotingToolData.estimatedCloseDate)
                    : null,
            );
            setQuoteDate(
                quotingToolData.quoteDate ? new Date(quotingToolData.quoteDate) : null,
            );
            setCarrier(quotingToolData.carrier ?? "");
            setCarrierRate(quotingToolData.carrierRate ?? "");
            setCarrierRateCurrency(quotingToolData.carrierRateCurrency ?? "");
            setCarrierRemarks(quotingToolData.carrierRemarks ?? "");
            setCurrency(quotingToolData.currency ?? "");
            setDescription(quotingToolData.description ?? "");
            setOceanPricingMode(quotingToolData.oceanPricingMode || "ALL_IN");
            const fetchedCarriers = quotingToolData.carriers?.length
                ? quotingToolData.carriers.map((carrier, carrierIndex) => ({
                    name: carrier.name || "",
                    chargeType: carrier.chargeType || "",
                    containerTypes: carrier.containerTypes || "",
                    containerPcs: carrier.containerPcs || "",
                    cost: carrier.cost || "",
                    currency: carrier.currency || "EUR",
                    sellingCarriers: carrier.sellingCarriers || "",
                    sellingCarriersRate: carrier.sellingCarriersRate || "EUR",

                    polCountry: carrier.polCountry
                        ? (findCountryOption(carrier.polCountry) || { label: carrier.polCountry })
                        : null,
                    pol: carrier.pol || (carrierIndex === 0 ? quotingToolData.pol ?? "" : ""),
                    polInput: carrier.pol || (carrierIndex === 0 ? quotingToolData.pol ?? "" : ""),
                    podCountry: carrier.podCountry
                        ? (findCountryOption(carrier.podCountry) || { label: carrier.podCountry })
                        : null,
                    pod: carrier.pod || (carrierIndex === 0 ? quotingToolData.pod ?? "" : ""),
                    podInput: carrier.pod || (carrierIndex === 0 ? quotingToolData.pod ?? "" : ""),
                    estimatedCloseDate: carrier.estimatedCloseDate
                        ? new Date(carrier.estimatedCloseDate)
                        : null,
                    transitTime: carrier.transitTime || "",
                    originFreeTime: carrier.originFreeTime || "",
                }))
                : [{
                    name: "",
                    chargeType: "",
                    containerPcs: "",
                    cost: "",
                    currency: "EUR",
                    sellingCarriers: "",
                    sellingCarriersRate: "EUR",
                    polCountry: null,
                    pol: "",
                    podCountry: null,
                    pod: "",
                    estimatedCloseDate: null,
                    transitTime: "",
                    originFreeTime: ""
                }];
            setCarriers(fetchedCarriers);
            setExtraCosts(
                Array.isArray(quotingToolData.extraCosts) && quotingToolData.extraCosts.length
                    ? quotingToolData.extraCosts
                    : [
                        { type: "", amount: "", currency: "USD", extraSelling: "", extraSellingRate: "USD" }
                    ]
            );
            setHydratingFromDb(true);

            const mode = quotingToolData.oceanPricingMode || "ALL_IN";
            const savedSelling = Number(quotingToolData.sallingRate || quotingToolData.sellingRate || 0);

            setOceanPricingMode(mode);

// ✅ Road mantığı: ALL_IN ise driver = SELLING (istersen PERCENT de yapabilirsin)
            if (mode === "ALL_IN") {
                setSellingDriver("SELLING");
                setSellingRateInput(savedSelling ? String(savedSelling) : "");
                setMarginPercentInput("");
                setMarginUnitInput("");
            } else {
                // BREAKDOWN: satır toplamı gösterilecek; inputları da doldur (read-only)
                setSellingDriver("SELLING");
                setSellingRateInput(savedSelling ? String(savedSelling) : "0");
                // margin inputlarını breakdown effect zaten dolduracak
            }

// hydration bitir
            setTimeout(() => setHydratingFromDb(false), 0);
            setSallingRate(quotingToolData.sallingRate ?? "");
            setMarginPercent(quotingToolData.marginPercent ?? "");
            setMarginPerUnit(quotingToolData.marginPerUnit ?? "");
            setCreatedSource(quotingToolData.createdSource || quotingToolData.source || quotingToolData.creationType || "manual");
            setPrefillRefId(quotingToolData.prefillRefId || null);
            // yalnızca tüm alanlar hatasız yüklendiyse kaydetmeye izin ver (yarım form DB yi ezmesin)
            savedRecordRef.current = quotingToolData;
            setEditDataLoaded(true);
        } catch (error) {
            console.error("Error fetching QuotingTool details:", error);
        } finally {
            setLoading(false);
        }
    };


    const normalizeCountryLabel = (s) =>
        String(s || "")
            .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
            .trim()
            .toLowerCase();

    const findCountryOption = (label) => {
        const want = normalizeCountryLabel(label);
        if (!want) return null;
        return countryOptions.find((c) => normalizeCountryLabel(c.label || c.name) === want) || null;
    };


    const prefillAppliedRef = useRef(false);

    useEffect(() => {
        if (!prefill) return;

        // Edit modundaysa (id varsa) RFQ prefill ile ezme
        if (quotingToolId) return;

        // options gelmeden set yapma
        if (!customerOptions?.length) return;
        if (!salesRepOptions?.length) return;

        // ✅ sadece 1 kez uygula
        if (prefillAppliedRef.current) return;
        prefillAppliedRef.current = true;

        // CUSTOMER
        if (prefill.customer?.name) {
            const target = String(prefill.customer.name).trim().toLowerCase();
            const found = customerOptions.find(
                (c) => String(c?.name || "").trim().toLowerCase() === target
            );

            if (found) {
                setSelectedCustomer(found);
                setInputValue(found.name);
                setOpen(false);
            }
        }

        // SALES REP
        if (Array.isArray(prefill.salesRepIds) && prefill.salesRepIds.length) {
            const idSet = new Set(prefill.salesRepIds.map((x) => String(x)));
            const reps = salesRepOptions.filter((r) => idSet.has(String(r?.id)));
            setSalesRep(reps);
        }

        // INCOTERM
        if (prefill.incoterm) setSelectedIncoterm(prefill.incoterm);

        // CARGO
        if (prefill.hsCode) setHsCode(prefill.hsCode);
        if (prefill.cargoDescription) setCargoDescription(prefill.cargoDescription);
        if (prefill.grossWeightKg != null) setGrossWeight(prefill.grossWeightKg);
        if (prefill.volumeCbm != null) setVolume(prefill.volumeCbm);
        if (prefill.serviceMode) setSelectedService(String(prefill.serviceMode).trim());
        if (prefill.pickupAddress || prefill.pickupAddress1) {
            setPickupAddress({
                ...emptyServiceAddress,
                ...(prefill.pickupAddress || prefill.pickupAddress1),
            });
        }
        if (prefill.deliveryAddress || prefill.deliveryAddress2) {
            setDeliveryAddress({
                ...emptyServiceAddress,
                ...(prefill.deliveryAddress || prefill.deliveryAddress2),
            });
        }
        if (prefill.valueOfCargoUnit != null) setQuantity(prefill.valueOfCargoUnit);

        // TRANSPORT MODE
        const tm = String(prefill.transportationMode || "");
        if (tm === "Ocean FCL") setOceanTransportType("FCL");
        if (tm === "Ocean LCL") setOceanTransportType("LCL");

        // QUOTE TYPE
        if (prefill.quoteType) setQuoteType(prefill.quoteType);

        // ---- POL / POD COUNTRY + PORT ----
        // ---- POL / POD COUNTRY + PORT ----
        const preCarrier = prefill?.carrier || {};

// ✅ COUNTRY fallback: originCountry yoksa origin kullan
        const originCountry = prefill?.originCountry || prefill?.origin || "";
        const destCountry = prefill?.destinationCountry || prefill?.destination || "";

// ✅ Port fallback (RFQ tarafında pol/pod yoksa city/port varyasyonları)
        const polCountryLabelRaw =
            preCarrier?.polCountry ??
            prefill?.polCountry ??
            originCountry ??
            "";

        const podCountryLabelRaw =
            preCarrier?.podCountry ??
            prefill?.podCountry ??
            destCountry ??
            "";

// ✅ Port name fallback: RFQ pol/pod yoksa originCity/destinationCity’yi de dene
        const polPortRaw =
            preCarrier?.pol ??
            prefill?.pol ??
            prefill?.originPort ??
            prefill?.polPort ??
            prefill?.portOfLoading ??
            prefill?.originSeaport ??
            prefill?.originCity ??        // ✅ ekle
            prefill?.origin?.port ??
            "";

        const podPortRaw =
            preCarrier?.pod ??
            prefill?.pod ??
            prefill?.destinationPort ??
            prefill?.podPort ??
            prefill?.portOfDischarge ??
            prefill?.destinationSeaport ??
            prefill?.destinationCity ??   // ✅ ekle
            prefill?.destination?.port ??
            "";

        const polCountryLabel = String(polCountryLabelRaw).trim();
        const podCountryLabel = String(podCountryLabelRaw).trim();
        const polPort = String(polPortRaw).trim();
        const podPort = String(podPortRaw).trim();

        const polCountryObj = polCountryLabel
            ? (findCountryOption(polCountryLabel) || { label: polCountryLabel })
            : null;

        const podCountryObj = podCountryLabel
            ? (findCountryOption(podCountryLabel) || { label: podCountryLabel })
            : null;

        if (polCountryObj || podCountryObj || polPort || podPort) {
            setCarriers((prev) => {
                const first = prev?.[0] || {};
                const nextFirst = {
                    ...first,

                    ...(polCountryObj ? { polCountry: polCountryObj } : {}),
                    ...(podCountryObj ? { podCountry: podCountryObj } : {}),
                    ...(polPort ? { pol: polPort, polInput: polPort } : {}),
                    ...(podPort ? { pod: podPort, podInput: podPort } : {}),
                };
                return [nextFirst, ...(prev?.slice(1) || [])];
            });

            // üst seviye alanları sadece varsa senkronla
            if (polPort) setPol(polPort);
            if (podPort) setPod(podPort);
        }

        // NOTES
        const noteParts = [prefill.specialInstructions, prefill.note].filter(Boolean);
        if (noteParts.length) setDescription(noteParts.join("\n"));

        // DATES
        if (prefill.etd) {
            const d = new Date(prefill.etd);
            if (!Number.isNaN(d.getTime())) setQuoteDate(d);
        }
        if (prefill.eta) {
            const d = new Date(prefill.eta);
            if (!Number.isNaN(d.getTime())) setEstimatedCloseDate(d);
        }

        // CONTAINER
        if (prefill.containerTypeLabel) {
            const ct = containerTypes.find((x) => x.label === prefill.containerTypeLabel);
            if (ct) setSelectedContainer(ct);
        }
    }, [
        prefill,
        quotingToolId,
        customerOptions,
        salesRepOptions,
        containerTypes,
        findCountryOption,
    ]);

    useEffect(() => {
        if (!prefill) return;
        if (quotingToolId) return; // edit modunda ezme

        setCreatedSource("prefill");

        // RFQ id / ref nerede ise onu yaz
        setPrefillRefId(
            prefill?.id ||
            prefill?.rfqId ||
            prefill?.rfqNumber ||
            prefill?.prefillRefId ||
            null
        );

    }, [prefill, quotingToolId]);


    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [salesRep, setSalesRep] = useState([]);
    const [selectedContainer, setSelectedContainer] = useState(null);
    const [consignee, setConsignee] = useState("");
    const [hsCode, setHsCode] = useState("");
    const [grossWeight, setGrossWeight] = useState("");
    const [volume, setVolume] = useState("");
    const [quantity, setQuantity] = useState("");
    const [cargoDescription, setCargoDescription] = useState("");
    const [pol, setPol] = useState("");
    const [pod, setPod] = useState("");
    const [finalDestination, setFinalDestination] = useState("");
    const [quoteNo, setQuoteNo] = useState("");
    const [transitTime, setTransitTime] = useState("");
    const [originFreeTime, setOriginFreeTime] = useState("");
    const [destinationFreeTime, setDestinationFreeTime] = useState("");
    const [selectedIncoterm, setSelectedIncoterm] = useState("");
    const [selectedStatus, setSelectedStatus] = useState("");
    const [selectedService, setSelectedService] = useState("");

    const emptyServiceAddress = {
        type: "door",
        country: "",
        city: "",
        additionalInfo: "",
    };

    const [pickupAddress, setPickupAddress] = useState(emptyServiceAddress);
    const [deliveryAddress, setDeliveryAddress] = useState(emptyServiceAddress);

    const emptyServiceCharge = {
        serviceProvider: "",
        cost: "",
        costCurrency: "EUR",
        sellingIncluded: false,
        selling: "",
        sellingCurrency: "EUR",
    };

    // Pickup and delivery pricing must stay independent.
    const [pickupService, setPickupService] = useState(emptyServiceCharge);
    const [deliveryService, setDeliveryService] = useState(emptyServiceCharge);

    const [pickupCityOptions, setPickupCityOptions] = useState([]);
    const [deliveryCityOptions, setDeliveryCityOptions] = useState([]);
    const [pickupCitiesLoading, setPickupCitiesLoading] = useState(false);
    const [deliveryCitiesLoading, setDeliveryCitiesLoading] = useState(false);

    const normalizedService = String(selectedService || "").trim();
    const showPickupAddress = ["Door to Port", "Door to Door"].includes(normalizedService);
    const showDeliveryAddress = ["Port to Door", "Door to Door"].includes(normalizedService);

    const normalizeLocationOptions = (payload) => {
        const raw = payload?.data?.data || payload?.data || payload || [];
        const list = Array.isArray(raw) ? raw : [];

        return list
            .map((item) => {
                if (typeof item === "string") return item.trim();
                return String(
                    item?.name ||
                    item?.label ||
                    item?.city ||
                    item?.value ||
                    ""
                ).trim();
            })
            .filter(Boolean)
            .filter((value, index, array) => array.indexOf(value) === index)
            .sort((a, b) => a.localeCompare(b));
    };

    useEffect(() => {
        let active = true;

        const loadPickupCities = async () => {
            if (!pickupAddress.country) {
                setPickupCityOptions([]);
                return;
            }

            setPickupCitiesLoading(true);
            try {
                const response = await CrudService.getCitiesByCountry(pickupAddress.country);
                if (active) setPickupCityOptions(normalizeLocationOptions(response));
            } catch (error) {
                console.error("Pickup cities could not be loaded:", error);
                if (active) setPickupCityOptions([]);
            } finally {
                if (active) setPickupCitiesLoading(false);
            }
        };

        loadPickupCities();
        return () => { active = false; };
    }, [pickupAddress.country]);

    useEffect(() => {
        let active = true;

        const loadDeliveryCities = async () => {
            if (!deliveryAddress.country) {
                setDeliveryCityOptions([]);
                return;
            }

            setDeliveryCitiesLoading(true);
            try {
                const response = await CrudService.getCitiesByCountry(deliveryAddress.country);
                if (active) setDeliveryCityOptions(normalizeLocationOptions(response));
            } catch (error) {
                console.error("Delivery cities could not be loaded:", error);
                if (active) setDeliveryCityOptions([]);
            } finally {
                if (active) setDeliveryCitiesLoading(false);
            }
        };

        loadDeliveryCities();
        return () => { active = false; };
    }, [deliveryAddress.country]);

    const updatePickupAddress = (field, value) => {
        setPickupAddress((prev) => ({
            ...prev,
            [field]: value,
            ...(field === "country" ? { city: "" } : {}),
        }));
    };

    const updateDeliveryAddress = (field, value) => {
        setDeliveryAddress((prev) => ({
            ...prev,
            [field]: value,
            ...(field === "country" ? { city: "" } : {}),
        }));
    };

    const updatePickupService = (field, value) => {
        setPickupService((prev) => ({
            ...prev,
            [field]: value,
            ...(field === "sellingIncluded" && value ? { selling: "" } : {}),
        }));
    };

    const updateDeliveryService = (field, value) => {
        setDeliveryService((prev) => ({
            ...prev,
            [field]: value,
            ...(field === "sellingIncluded" && value ? { selling: "" } : {}),
        }));
    };

    const [shipper, setShipper] = useState("");
    const [estimatedCloseDate, setEstimatedCloseDate] = useState(null);
    const [quoteDate, setQuoteDate] = useState(null);
    const [quoteType, setQuoteType] = useState(null);
    const [carrier, setCarrier] = useState("");
    const [carrierRate, setCarrierRate] = useState("");
    const [carrierRateCurrency, setCarrierRateCurrency] = useState("");
    const [carrierRemarks, setCarrierRemarks] = useState("");
    const [sallingRate, setSallingRate] = useState("");
    const [description, setDescription] = useState("");
    const [oceanTransportType, setOceanTransportType] = useState(null);
    const seaportReqRef = useRef({ pol: 0, pod: 0, final: 0 });
    const [marginPercent, setMarginPercent] = useState("");
    const [marginPerUnit, setMarginPerUnit] = useState("");
    const [totalBuying, setTotalBuying] = useState(0);
    const [totalExtraBuying, setTotalExtraBuying] = useState(0);
    const extraChargeOptions = EXTRA_CHARGE_TYPES[quoteType] || [];
// ✅ Selling Engine (Road ile aynı)
    const [sellingDriver, setSellingDriver] = useState("PERCENT"); // "PERCENT" | "UNIT" | "SELLING"
    const [activeSellingField, setActiveSellingField] = useState(null); // "PERCENT" | "UNIT" | "SELLING" | null

    const [marginPercentInput, setMarginPercentInput] = useState("");
    const [marginUnitInput, setMarginUnitInput] = useState("");
    const [sellingRateInput, setSellingRateInput] = useState("");

    const [hydratingFromDb, setHydratingFromDb] = useState(false);
    const [currency, setCurrency] = useState("USD");
    const baseCurrency = currency || "USD";
    const [createdSource, setCreatedSource] = useState("manual");
    const [prefillRefId, setPrefillRefId] = useState(null);
    // Pricing mode
    const [oceanPricingMode, setOceanPricingMode] = useState("ALL_IN");

    const [carriers, setCarriers] = useState([
        {
            id: 1,
            name: "",
            chargeType: "",
            containerTypes:"",
            containerPcs: "",
            cost: "",
            currency: "EUR",
            sellingCarriers: "",
            sellingCarriersRate: "EUR",
            polCountry: null,
            pol: "",
            polInput: "",
            polOptions: [],
            polLoading: false,
            podCountry: null,
            pod: "",
            podInput: "",
            podOptions: [],
            podLoading: false,
            estimatedCloseDate: null,
            transitTime: "",
            originFreeTime: "",
        }
    ]);



// Extra Charges
    const [extraCosts, setExtraCosts] = useState([
        {
            type: "",
            amount: "",
            currency: "USD",
            extraSelling: "",
            extraSellingRate: "USD",
        },
    ]);

// Totals
    const [totalCarrierSelling, setTotalCarrierSelling] = useState(0);
    const [totalExtraSelling, setTotalExtraSelling] = useState(0);


// cache (aynı ülke için tekrar çağrı yapmasın)
    const seaportCacheRef = useRef({}); // { "Turkey": [{label:"..."}] }

    useEffect(() => {
        if (isEditing || hydratingFromDb) return;

        setExtraCosts(prev =>
            prev.map(e => ({ ...e, type: "" }))
        );
    }, [quoteType, isEditing, hydratingFromDb]);

    useEffect(() => {
        carriers.slice(0, 1).forEach(async (c, index) => {
            const country = c.polCountry?.label;
            if (!country) return;

            const getCarrierTotals = (row) => {
                const buying = convertAmount(
                    Number(row.cost || 0),
                    row.currency || baseCurrency,
                    baseCurrency
                );

                const selling = convertAmount(
                    Number(row.sellingCarriers || 0),
                    row.sellingCarriersRate || baseCurrency,
                    baseCurrency
                );

                const profit = selling - buying;
                const marginPercent = buying > 0 ? (profit / buying) * 100 : 0;

                return {
                    buying,
                    selling,
                    profit,
                    marginPercent,
                };
            };

            handleCarrierChange(index, "polLoading", true);
            try {
                const res = await CrudService.getSeaportsByCountryOpp(country);
                const options = (res?.data?.data || [])
                    .map(item => ({ label: normalizePortLabel(item) }))
                    .filter(o => o.label);

                handleCarrierChange(index, "polOptions", options);
            } catch (e) {
                handleCarrierChange(index, "polOptions", []);
            } finally {
                handleCarrierChange(index, "polLoading", false);
            }
        });
    }, [carriers.map(c => c.polCountry?.label).join("|")]);

    useEffect(() => {
        carriers.slice(0, 1).forEach(async (c, index) => {
            const country = c.podCountry?.label;
            if (!country) return;

            handleCarrierChange(index, "podLoading", true);
            try {
                const res = await CrudService.getSeaportsByCountryOpp(country);
                const options = (res?.data?.data || [])
                    .map(item => ({ label: normalizePortLabel(item) }))
                    .filter(o => o.label);

                handleCarrierChange(index, "podOptions", options);
            } catch (e) {
                handleCarrierChange(index, "podOptions", []);
            } finally {
                handleCarrierChange(index, "podLoading", false);
            }
        });
    }, [carriers.map(c => c.podCountry?.label).join("|")]);


    const normalizePortLabel = (item) => {
        if (!item) return "";
        // API listesi string de dönebilir, object de
        if (typeof item === "string") return item.trim();
        return (
            item.name ||
            item.portName ||
            item.seaport ||
            item.label ||
            item.value ||
            ""
        ).toString().trim();
    };

    // On mount in CREATE mode, fetch next Quote No
    // Teklif numarasını kayıt sırasında backend verir (yeni teklifte alan "Auto-generated" gösterir)


    const handleSwitchChangeQuotingType = (type) => {
        setQuoteType((prevType) => (prevType === type ? "" : type));
    };
    const handleSwitchChangeTransportType = (type) => {
        setOceanTransportType((prevType) => (prevType === type ? "" : type));
    };

    const sanitizeCarriersForApi = (carriers) =>
        carriers.map(c => ({
            name: c.name || "",
            chargeType: c.chargeType || "",
            containerTypes: c.containerTypes || "",
            containerPcs: Number(c.containerPcs || 0),
            cost: Number(c.cost || 0),
            currency: c.currency || "USD",
            sellingCarriers: Number(c.sellingCarriers || 0),
            sellingCarriersRate: c.sellingCarriersRate || "USD",

            // 🔥 SADECE STRING
            polCountry: c.polCountry?.label || "",
            pol: c.pol || "",
            podCountry: c.podCountry?.label || "",
            pod: c.pod || "",
            estimatedCloseDate: c.estimatedCloseDate || null,
            transitTime: c.transitTime || "",
            originFreeTime: c.originFreeTime || ""
        }));

    const submitHandler = async (e) => {
        e.preventDefault();
        if (submittingRef.current) return;
        if (quotingToolId && !editDataLoaded) {
            setErrorMessage("Quote details could not be loaded. Please reload the page before saving.");
            window.scrollTo({ top: 0, behavior: "smooth" });
            return;
        }
        if (!selectedCustomer) {
            setErrorMessage("Please select a customer before saving.");
            window.scrollTo({ top: 0, behavior: "smooth" });
            return;
        }
        if (!quoteType) {
            setErrorMessage("Please select a quote type (Import / Export / Domestic / Transit) before saving.");
            window.scrollTo({ top: 0, behavior: "smooth" });
            return;
        }
        if (!carriers[0]?.pol?.trim() || !carriers[0]?.pod?.trim()) {
            setErrorMessage("Please enter the Port of Loading (POL) and Port of Discharge (POD) in the Route section.");
            window.scrollTo({ top: 0, behavior: "smooth" });
            return;
        }
        setErrorMessage("");
        submittingRef.current = true;

        const sanitizedCarriers = sanitizeCarriersForApi(carriers);
        const firstSanitizedCarrier = sanitizedCarriers[0] || {};

        const formatAddressText = (address) =>
            [
                address?.country,
                address?.city,
                address?.additionalInfo,
            ]
                .map((value) => String(value || "").trim())
                .filter(Boolean)
                .join(" / ");

        const pickupAddressPayload = showPickupAddress
            ? {
                ...emptyServiceAddress,
                ...pickupAddress,
                type: "door",
                country: pickupAddress?.country || "",
                city: pickupAddress?.city || "",
                additionalInfo: pickupAddress?.additionalInfo || "",
            }
            : {
                ...emptyServiceAddress,
            };

        const deliveryAddressPayload = showDeliveryAddress
            ? {
                ...emptyServiceAddress,
                ...deliveryAddress,
                type: "door",
                country: deliveryAddress?.country || "",
                city: deliveryAddress?.city || "",
                additionalInfo: deliveryAddress?.additionalInfo || "",
            }
            : {
                ...emptyServiceAddress,
            };

        const quotingToolData = {
            quoteType,
            oceanTransportType,
            customer: selectedCustomer?.name || "Unknown Customer",
            salesRep: salesRep.map((rep) => rep.id),

            container:
                firstSanitizedCarrier.containerTypes ||
                selectedContainer?.label ||
                "",

            transitTime:
                firstSanitizedCarrier.transitTime ||
                transitTime ||
                "",

            shipper,
            hsCode,
            consignee,
            grossWeight,
            volume,
            quantity,
            cargoDescription,
            // Rota üst seviyede de tutulur (tablolar / e-posta şablonu buradan da okur)
            pol: carriers[0]?.pol || "",
            pod: carriers[0]?.pod || "",
            polCountry: carriers[0]?.polCountry?.label || "",
            podCountry: carriers[0]?.podCountry?.label || "",
            finalDestination,
            quoteNo,
            estimatedCloseDate,
            quoteDate,
            incoterm: selectedIncoterm,
            status: selectedStatus,
            service: normalizedService,

            /*
             * Backend schema String bekliyor.
             */
            pickupAddress: showPickupAddress
                ? formatAddressText(pickupAddressPayload)
                : "",

            deliveryAddress: showDeliveryAddress
                ? formatAddressText(deliveryAddressPayload)
                : "",

            /*
             * Structured address alanları.
             * Backend schema bunları object olarak destekliyorsa kullanılır.
             */
            pickupAddress1: showPickupAddress ? pickupAddressPayload : null,
            deliveryAddress2: showDeliveryAddress ? deliveryAddressPayload : null,

            pickupService: showPickupAddress
                ? {
                    serviceProvider: pickupService.serviceProvider || "",
                    cost: Number(pickupService.cost || 0),
                    costCurrency: pickupService.costCurrency || "EUR",
                    sellingIncluded: Boolean(pickupService.sellingIncluded),
                    selling: pickupService.sellingIncluded
                        ? 0
                        : Number(pickupService.selling || 0),
                    sellingCurrency: pickupService.sellingCurrency || "EUR",
                }
                : null,

            deliveryService: showDeliveryAddress
                ? {
                    serviceProvider: deliveryService.serviceProvider || "",
                    cost: Number(deliveryService.cost || 0),
                    costCurrency: deliveryService.costCurrency || "EUR",
                    sellingIncluded: Boolean(deliveryService.sellingIncluded),
                    selling: deliveryService.sellingIncluded
                        ? 0
                        : Number(deliveryService.selling || 0),
                    sellingCurrency: deliveryService.sellingCurrency || "EUR",
                }
                : null,

            originFreeTime,
            destinationFreeTime,
            carrier,
            carrierRate,
            carrierRateCurrency,
            carrierRemarks,
            description,
            selectedTransportType: selectedTransportType || "Ocean",
            oceanPricingMode,
            carriers: sanitizedCarriers,
            extraCosts,
            totalCarrierSelling,
            totalExtraSelling,
            sallingRate: Number(sellingCalc.finalSelling) || 0,
            marginPercent: marginPercentInput,
            marginPerUnit: marginUnitInput,
            // Carrier #1 Cost/Selling currencies are the quote reference currencies.
            currency:
                firstSanitizedCarrier.sellingCarriersRate ||
                baseCurrency,
            buyingCurrency:
                firstSanitizedCarrier.currency ||
                baseCurrency,
            sellingCurrency:
                firstSanitizedCarrier.sellingCarriersRate ||
                baseCurrency,
            totalBuyingCurrency:
                firstSanitizedCarrier.currency ||
                baseCurrency,
            totalSellingCurrency:
                firstSanitizedCarrier.sellingCarriersRate ||
                baseCurrency,
            totalBuying,
            totalExtraBuying,
            createdSource,
            prefillRefId,
        };
        // Fiyat girdileri değişmediyse kayıtlı fiyat/marj/toplamlar korunur (kur veya kural farkı fiyatı değiştirmesin)
        if (isEditing) keepSavedPricingIfUntouched(quotingToolData, savedRecordRef.current, useLineSelling);

        try {
            setLoading(true);

            let response;

            if (isEditing) {
                response = await CrudService.updateQuotingTool(
                    quotingToolData,
                    quotingToolId
                );

                setSuccessMessage(
                    "Update operation was successfully completed."
                );
            } else {
                response = await CrudService.createQuotingTool({
                    data: quotingToolData,
                });

                const newQuoteNo =
                    response.data?.data?.attributes?.quoteNo;

                if (newQuoteNo) {
                    setQuoteNo(newQuoteNo);
                }

                setSuccessMessage(
                    "Create operation was successfully completed."
                );
            }

            window.scrollTo({
                top: 0,
                behavior: "smooth",
            });

            redirectTimerRef.current = setTimeout(() => {
                navigate(
                    "/quoting-tool/quoting-tool-tables/details"
                );
            }, 3000);
        } catch (err) {
            console.error(
                "Request failed:",
                err?.response?.data || err
            );
            submittingRef.current = false;
            setErrorMessage(err?.errors?.[0]?.detail || err?.message || "Save failed. Please try again.");
            window.scrollTo({ top: 0, behavior: "smooth" });
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        const fetchServiceProviders = async () => {
            try {
                const response = await CrudService.getServiceProviders();
                const options = (response.data ?? []).map(
                    (item) => item?.attributes?.name || "Unnamed Service Provider"
                );
                setServiceProviderOptions(options);
            } catch (error) {
                console.error("Service Provider verileri alınırken hata oluştu:", error);
                setServiceProviderOptions([]);
            }
        };
        fetchServiceProviders();
    }, []);

    const inputStyle = {"& .MuiOutlinedInput-input": { padding: "10 14px" },};

    const clearLineSelling = React.useCallback(() => {
        setCarriers((prev) =>
            (prev || []).map((c) => ({
                ...c,
                sellingCarriers: "",
                // istersen rate kalsın:
                sellingCarriersRate: c.sellingCarriersRate || baseCurrency,
            }))
        );

        setExtraCosts((prev) =>
            (prev || []).map((e) => ({
                ...e,
                extraSelling: "",
                extraSellingRate: e.extraSellingRate || baseCurrency,
            }))
        );

        setPickupService((prev) => ({
            ...prev,
            selling: "",
            sellingCurrency: prev.sellingCurrency || baseCurrency,
        }));

        setDeliveryService((prev) => ({
            ...prev,
            selling: "",
            sellingCurrency: prev.sellingCurrency || baseCurrency,
        }));
    }, [baseCurrency]);

    useEffect(() => {
        if (!exchangeRates) return;

        const buyingReferenceCurrency =
            carriers?.[0]?.currency || baseCurrency;

        const sellingReferenceCurrency =
            carriers?.[0]?.sellingCarriersRate || baseCurrency;

        const carrierBuying = carriers.reduce((sum, c) => {
            const amount = Number(c.cost || 0);
            return sum + convertAmount(
                amount,
                c.currency || buyingReferenceCurrency,
                buyingReferenceCurrency
            );
        }, 0);

        const extraBuying = extraCosts.reduce((sum, e) => {
            const amount = Number(e.amount || 0);
            return sum + convertAmount(
                amount,
                e.currency || buyingReferenceCurrency,
                buyingReferenceCurrency
            );
        }, 0);

        const pickupBuying = showPickupAddress
            ? convertAmount(
                Number(pickupService.cost || 0),
                pickupService.costCurrency || buyingReferenceCurrency,
                buyingReferenceCurrency
            )
            : 0;

        const deliveryBuying = showDeliveryAddress
            ? convertAmount(
                Number(deliveryService.cost || 0),
                deliveryService.costCurrency || buyingReferenceCurrency,
                buyingReferenceCurrency
            )
            : 0;

        setTotalBuying(carrierBuying + extraBuying + pickupBuying + deliveryBuying);
        setTotalExtraBuying(extraBuying);

        const carrierSelling = carriers.reduce((sum, c) => {
            const amount = Number(c.sellingCarriers || 0);
            return sum + convertAmount(
                amount,
                c.sellingCarriersRate || sellingReferenceCurrency,
                sellingReferenceCurrency
            );
        }, 0);

        const extraSelling = extraCosts.reduce((sum, e) => {
            const amount = Number(e.extraSelling || 0);
            return sum + convertAmount(
                amount,
                e.extraSellingRate || sellingReferenceCurrency,
                sellingReferenceCurrency
            );
        }, 0);

        const pickupSelling = showPickupAddress && !pickupService.sellingIncluded
            ? convertAmount(
                Number(pickupService.selling || 0),
                pickupService.sellingCurrency || sellingReferenceCurrency,
                sellingReferenceCurrency
            )
            : 0;

        const deliverySelling = showDeliveryAddress && !deliveryService.sellingIncluded
            ? convertAmount(
                Number(deliveryService.selling || 0),
                deliveryService.sellingCurrency || sellingReferenceCurrency,
                sellingReferenceCurrency
            )
            : 0;

        setTotalCarrierSelling(carrierSelling);
        setTotalExtraSelling(extraSelling + pickupSelling + deliverySelling);
    }, [
        carriers,
        extraCosts,
        pickupService,
        deliveryService,
        showPickupAddress,
        showDeliveryAddress,
        baseCurrency,
        exchangeRates,
    ]);

    const toNum = (v) => {
        const n = Number(v);
        return Number.isFinite(n) ? n : 0;
    };

    const hasAnySellingInput = React.useMemo(() => {
        const hasCarrier = (carriers || []).some((c) => Number(c?.sellingCarriers) > 0);
        const hasExtra = (extraCosts || []).some((e) => Number(e?.extraSelling) > 0);
        const hasPickup = showPickupAddress && !pickupService.sellingIncluded && Number(pickupService.selling) > 0;
        const hasDelivery = showDeliveryAddress && !deliveryService.sellingIncluded && Number(deliveryService.selling) > 0;
        return hasCarrier || hasExtra || hasPickup || hasDelivery;
    }, [
        carriers,
        extraCosts,
        pickupService,
        deliveryService,
        showPickupAddress,
        showDeliveryAddress,
    ]);

    const breakdownSellingTotal = toNum(totalCarrierSelling) + toNum(totalExtraSelling);
    const useLineSelling = oceanPricingMode === "BREAKDOWN" || hasAnySellingInput;
    const sellingCalc = React.useMemo(() => {
        const buying = toNum(totalBuying);

        if (useLineSelling) {
            const finalSelling = toNum(breakdownSellingTotal);
            const marginUnit = finalSelling - buying;
            const marginPercent = buying > 0 ? (marginUnit / buying) * 100 : 0;

            return {
                finalSelling,
                finalMarginUnit: Number.isFinite(marginUnit) ? marginUnit : 0,
                finalMarginPercent: Number.isFinite(marginPercent) ? marginPercent : 0,
            };
        }

        const pct = Number(marginPercentInput);
        const unit = Number(marginUnitInput);
        const manual = Number(sellingRateInput);

        let finalSelling = buying;
        let finalMarginUnit = 0;
        let finalMarginPercent = 0;

        if (sellingDriver === "PERCENT" && marginPercentInput !== "" && !Number.isNaN(pct)) {
            finalMarginPercent = pct;
            finalMarginUnit = (buying * pct) / 100;
            finalSelling = buying + finalMarginUnit;
        } else if (sellingDriver === "UNIT" && marginUnitInput !== "" && !Number.isNaN(unit)) {
            finalMarginUnit = unit;
            finalMarginPercent = buying > 0 ? (unit / buying) * 100 : 0;
            finalSelling = buying + unit;
        } else if (sellingDriver === "SELLING" && sellingRateInput !== "" && !Number.isNaN(manual)) {
            finalSelling = manual;
            finalMarginUnit = manual - buying;
            finalMarginPercent = buying > 0 ? (finalMarginUnit / buying) * 100 : 0;
        }

        return {
            finalSelling,
            finalMarginUnit: Number.isFinite(finalMarginUnit) ? finalMarginUnit : 0,
            finalMarginPercent: Number.isFinite(finalMarginPercent) ? finalMarginPercent : 0,
        };
    }, [
        useLineSelling,
        sellingDriver,
        marginPercentInput,
        marginUnitInput,
        sellingRateInput,
        totalBuying,
        breakdownSellingTotal,
    ]);

    useEffect(() => {
        if (hydratingFromDb) return;
        if (useLineSelling) return;

        const buying = toNum(totalBuying);
        if (!Number.isFinite(buying)) return;

        const safeSet = (setter, next) => {
            const n = next == null ? "" : String(next);
            setter((prev) => (String(prev) === n ? prev : n));
        };

        const source = activeSellingField || sellingDriver;

        if (source === "PERCENT") {
            if (marginPercentInput === "") return;
            const pct = Number(marginPercentInput);
            if (Number.isNaN(pct)) return;

            const unit = (buying * pct) / 100;
            safeSet(setMarginUnitInput, unit.toFixed(2));
            safeSet(setSellingRateInput, (buying + unit).toFixed(2));
            return;
        }

        if (source === "UNIT") {
            if (marginUnitInput === "") return;
            const unit = Number(marginUnitInput);
            if (Number.isNaN(unit)) return;

            const pct = buying > 0 ? (unit / buying) * 100 : 0;
            safeSet(setMarginPercentInput, pct.toFixed(2));
            safeSet(setSellingRateInput, (buying + unit).toFixed(2));
            return;
        }

        if (source === "SELLING") {
            if (sellingRateInput === "") return;
            const manual = Number(sellingRateInput);
            if (Number.isNaN(manual)) return;

            const unit = manual - buying;
            const pct = buying > 0 ? (unit / buying) * 100 : 0;
            safeSet(setMarginUnitInput, unit.toFixed(2));
            safeSet(setMarginPercentInput, pct.toFixed(2));
        }
    }, [
        hydratingFromDb,
        useLineSelling,
        totalBuying,
        activeSellingField,
        sellingDriver,
        marginPercentInput,
        marginUnitInput,
        sellingRateInput,
    ]);

    useEffect(() => {
        if (hydratingFromDb) return;
        if (!useLineSelling) return;
        if (activeSellingField) return;

        const buying = toNum(totalBuying);
        const selling = toNum(breakdownSellingTotal);

        const unit = selling - buying;
        const pct = buying > 0 ? (unit / buying) * 100 : 0;

        const safeSet = (setter, next) => {
            const v = next == null ? "" : String(next);
            setter((prev) => (String(prev) === v ? prev : v));
        };

        safeSet(setSellingRateInput, selling.toFixed(2));
        safeSet(setMarginUnitInput, unit.toFixed(2));
        safeSet(setMarginPercentInput, pct.toFixed(2));

        setSellingDriver("SELLING");
    }, [
        hydratingFromDb,
        useLineSelling,
        activeSellingField,
        breakdownSellingTotal,
        totalBuying,
    ]);
    const handleCarrierChange = (
        index,
        field,
        value
    ) => {
        setCarriers((prev) =>
            prev.map((carrierRow, carrierIndex) =>
                carrierIndex === index
                    ? {
                        ...carrierRow,
                        [field]: value,
                    }
                    : carrierRow
            )
        );
        if (
            index === 0 &&
            field === "sellingCarriersRate"
        ) {
            setCurrency(
                String(value || "USD")
                    .trim()
                    .toUpperCase()
            );
        }
    };
    // POL / POD tüm carrier satırları için ortak rota: carrier bloğunun üstünde bir kez girilir ve
    // her satıra yazılır (liste, dashboard ve e-posta şablonu carrier satırından okumaya devam eder).
    const ROUTE_FIELDS = ["polCountry", "pol", "polInput", "podCountry", "pod", "podInput"];
    const route = carriers[0] || {};
    const handleRouteChange = (patch) => {
        setCarriers((prev) => prev.map((c) => ({ ...c, ...patch })));
    };
    const routeKey = (c) => [c?.polCountry?.label, c?.pol, c?.podCountry?.label, c?.pod].join("|");
    // Eski kayıtlarda carrier başına farklı liman olabilir; rota değişmedikçe satırlar korunur
    const routesDiffer = carriers.some((c) => routeKey(c) !== routeKey(carriers[0]));
    const routeOf = (row) => Object.fromEntries(ROUTE_FIELDS.map((k) => [k, row?.[k] ?? (k.endsWith("Country") ? null : "")]));

    const addCarrier = () => {
        setCarriers(prev => [
            ...prev,
            {
                id: prev.length + 1,
                name: "",
                chargeType: "",
                containerPcs: "",
                cost: "",
                currency: "EUR",
                sellingCarriers: "",
                sellingCarriersRate: "EUR",
                polCountry: null,
                pol: "",
                polInput: "",
                polOptions: [],
                polLoading: false,
                podCountry: null,
                pod: "",
                podInput: "",
                podOptions: [],
                podLoading: false,
                estimatedCloseDate: null,
                transitTime: "",
                originFreeTime: "",
                ...routeOf(prev[0]),
            }
        ]);
    };


    const removeCarrier = (index) => {
        setCarriers(prev => prev.filter((_, i) => i !== index));
    };

    const handleExtraChange = (index, field, value) => {
        setExtraCosts(prev =>
            prev.map((e, i) =>
                i === index ? { ...e, [field]: value } : e
            )
        );
    };
    // Not: liman listesi ülke seçilince bir kez çekilir (backend arama parametresi desteklemiyor);
    // Autocomplete yazılan metne göre listeyi kendi içinde filtreler.



    const addExtra = () => {
        setExtraCosts(prev => [
            ...prev,
            { type: "", amount: "", currency: "USD", extraSelling: "", extraSellingRate: "USD" }
        ]);
    };

    const removeExtra = (index) => {
        setExtraCosts(prev => prev.filter((_, i) => i !== index));
    };

    useEffect(() => {
        if (isEditing && selectedCustomer) {
            setInputValue(selectedCustomer.name);
            setOpen(false);
        }
    }, [isEditing, selectedCustomer]);


    const normalizeSummaryCurrency = (value, fallback = "USD") =>
        String(value || fallback || "USD")
            .trim()
            .toUpperCase();

    const toSummaryNumber = (value) => {
        const parsedValue = Number(value);

        return Number.isFinite(parsedValue)
            ? parsedValue
            : 0;
    };

    const convertSummaryAmount = (
        amount,
        fromCurrency,
        toCurrency
    ) => {
        const numericAmount = toSummaryNumber(amount);

        const from = normalizeSummaryCurrency(
            fromCurrency,
            baseCurrency
        );

        const to = normalizeSummaryCurrency(
            toCurrency,
            baseCurrency
        );

        if (numericAmount === 0 || from === to) {
            return numericAmount;
        }

        /*
         * Kur verileri henüz yüklenmediyse yanlış toplam
         * göstermek yerine null döndürüyoruz.
         */
        if (!exchangeRates) {
            return null;
        }

        const fromRate =
            from === "USD"
                ? 1
                : Number(exchangeRates[from]);

        const toRate =
            to === "USD"
                ? 1
                : Number(exchangeRates[to]);

        if (
            !Number.isFinite(fromRate) ||
            fromRate <= 0 ||
            !Number.isFinite(toRate) ||
            toRate <= 0
        ) {
            return null;
        }

        /*
         * API base currency: USD
         *
         * Source Currency -> USD -> Target Currency
         */
        const amountInUSD = numericAmount / fromRate;

        return amountInUSD * toRate;
    };

    const formatSummaryMoney = (
        amount,
        currencyCode
    ) => {
        const normalizedCurrency =
            normalizeSummaryCurrency(
                currencyCode,
                baseCurrency
            );

        if (amount === null) {
            return `Rate unavailable (${normalizedCurrency})`;
        }

        return `${toSummaryNumber(amount).toFixed(
            2
        )} ${normalizedCurrency}`;
    };

    return (
        <Grid container spacing={0} justifyContent="center" mb={1}>
            <Grid item xs={12} lg={10}>
                <MDBox mb={2}>
                    <Grid container justifyContent="center">
                        <Grid item xs={12} lg={12}>
                            <MDBox mb={1} textAlign="center">
                                <div
                                    ref={successMessageRef}
                                    style={{
                                        backgroundColor: successMessage ? "#7edd82" : errorMessage ? "#e57373" : "transparent",
                                        color: successMessage || errorMessage ? "#FFFFFF" : "transparent",
                                        padding: "10px",
                                        textAlign: "center",
                                        marginBottom: "10px",
                                        borderRadius: "5px",
                                        visibility: successMessage || errorMessage ? "visible" : "hidden",
                                    }}
                                >
                                    {successMessage || errorMessage}
                                </div>
                                {isEditing && editDataLoaded && savedPricingNotice(savedRecordRef.current, sellingCalc.finalSelling) && (
                                    <div
                                        style={{
                                            backgroundColor: "#fff3cd",
                                            color: "#664d03",
                                            padding: "10px",
                                            textAlign: "center",
                                            marginBottom: "10px",
                                            borderRadius: "5px",
                                        }}
                                    >
                                        {savedPricingNotice(savedRecordRef.current, sellingCalc.finalSelling)}
                                    </div>
                                )}
                                <MDBox mb={1}>
                                    <MDTypography variant="h3" fontWeight="bold">
                                        {isEditing
                                            ? "Edit Ocean Quote Details"
                                            : "Add Ocean Quote Details"}
                                    </MDTypography>
                                </MDBox>
                                <MDTypography variant="h5" fontWeight="regular" color="secondary">
                                    This information will give you more information about the Quote for Ocean.
                                </MDTypography>
                            </MDBox>
                        </Grid>
                    </Grid>
                </MDBox>
                <MDBox mt={3}
                       sx={{
                           p: 3,
                           borderRadius: "10px",
                           bgcolor: "background.paper",
                           boxShadow: "none",
                       }}>
                    <MDBox mb={3}>
                        <Grid container justifyContent="center">
                            <Grid item xs={12} lg={12}>
                                <MDBox>
                                    <MDBox mt={3}>
                                        <Grid container spacing={3} alignItems="center">
                                            <Grid item xs={6}>
                                                <MDTypography variant="h6" component="div">
                                                    Quote - Ocean {quoteNo}
                                                </MDTypography>
                                            </Grid>
                                        </Grid>
                                    </MDBox>
                                    <Box
                                        mt={3}
                                        display="flex"
                                        justifyContent="flex-start"
                                        alignItems="center"
                                        gap={4}
                                    >
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={quoteType === "Import"}
                                                    onChange={() => handleSwitchChangeQuotingType("Import")}
                                                    name="importSwitch"
                                                    sx={{
                                                        "& .MuiSwitch-thumb": {
                                                            backgroundColor: quoteType === "Import" ? "orange" : "white",
                                                        },
                                                        "& .MuiSwitch-track": {
                                                            backgroundColor: quoteType === "Import" ? "orange" : "white",
                                                        },
                                                    }}
                                                />
                                            }
                                            label="Import"
                                            sx={{ color: "gray" }}
                                        />
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={quoteType === "Export"}
                                                    onChange={() => handleSwitchChangeQuotingType("Export")}
                                                    name="exportSwitch"
                                                    sx={{
                                                        "& .MuiSwitch-thumb": {
                                                            backgroundColor: quoteType === "Export" ? "orange" : "white",
                                                        },
                                                        "& .MuiSwitch-track": {
                                                            backgroundColor: quoteType === "Export" ? "orange" : "white",
                                                        },
                                                    }}
                                                />
                                            }
                                            label="Export"
                                            sx={{ color: "gray" }}
                                        />
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={quoteType === "Transit"}
                                                    onChange={() => handleSwitchChangeQuotingType("Transit")}
                                                    name="transitSwitch"
                                                    sx={{
                                                        "& .MuiSwitch-thumb": {
                                                            backgroundColor: quoteType === "Transit" ? "orange" : "white",
                                                        },
                                                        "& .MuiSwitch-track": {
                                                            backgroundColor: quoteType === "Transit" ? "orange" : "white",
                                                        },
                                                    }}
                                                />
                                            }
                                            label="Transit"
                                            sx={{ color: "gray" }}
                                        />
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={oceanTransportType === "FCL"}
                                                    onChange={() => handleSwitchChangeTransportType("FCL")}
                                                    name="fclSwitch"
                                                    sx={{
                                                        "& .MuiSwitch-thumb": {
                                                            backgroundColor: oceanTransportType === "FCL" ? "orange" : "white",
                                                        },
                                                        "& .MuiSwitch-track": {
                                                            backgroundColor:
                                                                oceanTransportType === "FCL" ? "#FFECB3" : "#E0E0E0",
                                                        },
                                                    }}
                                                />
                                            }
                                            label="FCL"
                                            sx={{ color: "gray", ml: 10 }}
                                        />
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={oceanTransportType === "LCL"}
                                                    onChange={() => handleSwitchChangeTransportType("LCL")}
                                                    name="lclSwitch"
                                                    sx={{
                                                        "& .MuiSwitch-thumb": {
                                                            backgroundColor: oceanTransportType === "LCL" ? "orange" : "white",
                                                        },
                                                        "& .MuiSwitch-track": {
                                                            backgroundColor:
                                                                oceanTransportType === "LCL" ? "#FFECB3" : "#E0E0E0",
                                                        },
                                                    }}
                                                />
                                            }
                                            label="LCL"
                                            sx={{ color: "gray" }}
                                        />
                                    </Box>
                                    <MDBox mt={3}>
                                        <Grid container spacing={1}>
                                            <Grid item xs={12} sm={2}>
                                                <TextField
                                                    type="text"
                                                    label="Quote No"
                                                    value={quoteNo}
                                                    placeholder="Auto-generated"
                                                    disabled
                                                    fullWidth
                                                    InputLabelProps={{ shrink: true }}
                                                    sx={{ "& .MuiInputBase-root": { height: 40 } }}
                                                    onChange={(e) => {
                                                        const value = e.target.value;
                                                        if (value === "" || Number(value) >= 0) setQuoteNo(value);
                                                    }}
                                                />
                                            </Grid>

                                            <Grid item xs={12} sm={2}>
                                                <TextField
                                                    type="text"
                                                    label="Transport Type"
                                                    value={selectedTransportType}
                                                    disabled
                                                    fullWidth
                                                    InputLabelProps={{ shrink: true }}
                                                    sx={{ "& .MuiInputBase-root": { height: 40 } }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={2}>
                                                <LocalizationProvider dateAdapter={AdapterDateFns} locale={tr}>
                                                    <DatePicker
                                                        label="Quote Date"
                                                        value={quoteDate}
                                                        onChange={(newValue) => setQuoteDate(newValue)}
                                                        format="dd/MM/yyyy"
                                                        slotProps={{
                                                            textField: {
                                                                fullWidth: true,
                                                                variant: "outlined",
                                                                InputLabelProps: { shrink: true },
                                                                sx: { "& .MuiInputBase-root": { height: 40 } }
                                                            }
                                                        }}
                                                    />
                                                </LocalizationProvider>
                                            </Grid>
                                            {/* Destination Free Time */}
                                            <Grid item xs={12} sm={2}>
                                                <TextField
                                                    fullWidth
                                                    label="Destination Free Time (Days)"
                                                    value={destinationFreeTime}
                                                    onChange={(e) => setDestinationFreeTime(e.target.value)}
                                                    InputLabelProps={{ shrink: true }}
                                                    sx={{ "& .MuiInputBase-root": { height: 40 } }}
                                                />
                                            </Grid>

                                            {/* Incoterm */}
                                            <Grid item xs={12} sm={2}>
                                                <Autocomplete
                                                    options={incotermsOptions}
                                                    getOptionLabel={(option) => option.label}
                                                    value={selectedIncoterm ? { label: selectedIncoterm } : null}
                                                    onChange={(e, v) => setSelectedIncoterm(v ? v.label : "")}
                                                    isOptionEqualToValue={(o, v) => o.label === v.label}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            label="Incoterm"
                                                            InputLabelProps={{ shrink: true }}
                                                            sx={{ "& .MuiInputBase-root": { height: 40 } }}
                                                        />
                                                    )}
                                                />
                                            </Grid>

                                            {/* Service Level */}
                                            <Grid item xs={12} sm={2}>
                                                <Autocomplete
                                                    options={serviceOptions}
                                                    getOptionLabel={(option) => option.label}
                                                    value={serviceOptions.find((option) => option.label === normalizedService) || null}
                                                    onChange={(e, v) => setSelectedService(v?.label || "")}
                                                    isOptionEqualToValue={(o, v) => o.label === v.label}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            label="Service Level"
                                                            InputLabelProps={{ shrink: true }}
                                                            sx={{ "& .MuiInputBase-root": { height: 40 } }}
                                                        />
                                                    )}
                                                />
                                            </Grid>
                                        </Grid>
                                    </MDBox>
                                    <MDBox mt={3}>
                                        <Grid container spacing={3}>
                                            <Grid item xs={12} sm={4}>
                                                <Autocomplete
                                                    value={selectedCustomer}
                                                    onChange={(event, newValue) => {
                                                        if (newValue?.type === "new") {
                                                            setCustomerModalTarget("customer");
                                                            setCustomerModalOpen(true);
                                                            return;
                                                        }

                                                        setSelectedCustomer(newValue);
                                                        setOpen(false);
                                                    }}
                                                    inputValue={inputValue}
                                                    onInputChange={(event, newInputValue) => {
                                                        setInputValue(newInputValue);
                                                        setOpen(newInputValue.length > 0);
                                                    }}
                                                    options={[
                                                        NEW_PARTY_OPTION,
                                                        ...partyOptions,
                                                        // listede olmayan (silinmiş / adı değişmiş) kayıtlı müşteri de seçili görünsün
                                                        ...(selectedCustomer?.name && !partyOptions.some((o) => o.name === selectedCustomer.name) ? [selectedCustomer] : []),
                                                    ]}
                                                    getOptionLabel={(option) => option?.name || ""}
                                                    filterOptions={(options, state) =>
                                                        options.filter((option) =>
                                                            (option.name || "").toLowerCase().includes(state.inputValue.toLowerCase())
                                                        )
                                                    }
                                                    renderOption={({ key: optionKey, ...props }, option) => (
                                                        <li {...props} key={`${option.type}-${option.id}`}>
                                                            {option.type === "new" ? (
                                                                <strong style={{ color: "#1976d2" }}>
                                                                    + New Account
                                                                </strong>
                                                            ) : (
                                                                <>
                                                                    {option.name}
                                                                    <span style={{ marginLeft: 8, opacity: 0.7, fontSize: 12 }}>
                                                                            {option.type === "customer"
                                                                                ? "(Customer)"
                                                                                : "(Global Agent)"}
                                                                        </span>
                                                                </>
                                                            )}
                                                        </li>
                                                    )}
                                                    open={open}
                                                    onClose={() => setOpen(false)}
                                                    onFocus={() => {
                                                        if (!isEditing) setOpen(true);
                                                    }}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            label="Search Customer / Global Agent"
                                                            variant="outlined"
                                                            InputLabelProps={{ shrink: true }}
                                                            sx={{
                                                                "& .MuiInputBase-root": { height: 50 },
                                                            }}
                                                            InputProps={{
                                                                ...params.InputProps,
                                                            }}
                                                        />
                                                    )}
                                                />

                                            </Grid>
                                            <Grid item xs={12} sm={3}>
                                                <Autocomplete
                                                    options={[NEW_PARTY_OPTION, ...shipperConsigneeOptions]}
                                                    value={findOptionByName(shipper)}
                                                    onChange={(event, newValue) => {
                                                        if (newValue?.type === "new") {
                                                            setCustomerModalTarget("shipper");
                                                            setCustomerModalOpen(true);
                                                            return;
                                                        }

                                                        setShipper(newValue?.name || "");
                                                    }}
                                                    getOptionLabel={(option) => option?.name || ""}
                                                    isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                                                    renderOption={({ key: optionKey, ...props }, option) => (
                                                        <li {...props} key={option.id}>
                                                            {option.type === "new" ? (
                                                                <strong style={{ color: "#1976d2" }}>
                                                                    + New Account
                                                                </strong>
                                                            ) : (
                                                                <>
                                                                    {option.name}
                                                                    <span style={{ marginLeft: 8, opacity: 0.7, fontSize: 12 }}>
                                                                        {option.type === "customer"
                                                                            ? "(Customer)"
                                                                            : option.type === "agent"
                                                                                ? "(Global Agent)"
                                                                                : "(Company)"}
                                                                    </span>
                                                                </>
                                                            )}
                                                        </li>
                                                    )}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            label="Shipper"
                                                            fullWidth
                                                            InputLabelProps={{ shrink: true }}
                                                        />
                                                    )}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={3}>
                                                <Autocomplete
                                                    options={[NEW_PARTY_OPTION, ...shipperConsigneeOptions]}
                                                    value={findOptionByName(consignee)}
                                                    onChange={(event, newValue) => {
                                                        if (newValue?.type === "new") {
                                                            setCustomerModalTarget("consignee");
                                                            setCustomerModalOpen(true);
                                                            return;
                                                        }

                                                        setConsignee(newValue?.name || "");
                                                    }}
                                                    getOptionLabel={(option) => option?.name || ""}
                                                    isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                                                    renderOption={({ key: optionKey, ...props }, option) => (
                                                        <li {...props} key={option.id}>
                                                            {option.type === "new" ? (
                                                                <strong style={{ color: "#1976d2" }}>
                                                                    + New Account
                                                                </strong>
                                                            ) : (
                                                                <>
                                                                    {option.name}
                                                                    <span style={{ marginLeft: 8, opacity: 0.7, fontSize: 12 }}>
                                                                                {option.type === "customer"
                                                                                    ? "(Customer)"
                                                                                    : option.type === "agent"
                                                                                        ? "(Global Agent)"
                                                                                        : "(Company)"}
                                                                            </span>
                                                                </>
                                                            )}
                                                        </li>
                                                    )}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            label="Consignee"
                                                            fullWidth
                                                            InputLabelProps={{ shrink: true }}
                                                        />
                                                    )}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={2}>
                                                <Autocomplete
                                                    multiple
                                                    options={salesRepOptions}
                                                    getOptionLabel={(option) => option.label}
                                                    value={salesRep}
                                                    onChange={(e, v) => setSalesRep(v)}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            label="Sales Representatives"
                                                            fullWidth
                                                            InputLabelProps={{ shrink: true }}
                                                        />
                                                    )}
                                                />
                                            </Grid>
                                        </Grid>
                                    </MDBox>

                                    {(showPickupAddress || showDeliveryAddress) && (
                                        <MDBox
                                            mt={3}
                                            p={2.5}
                                            sx={{
                                                borderRadius: 2,
                                                border: "1px solid #dbeafe",
                                                backgroundColor: "#f8fbff",
                                            }}
                                        >
                                            <MDBox mb={2}>
                                                <MDTypography variant="h6" fontWeight="bold">
                                                    Service Address Details
                                                </MDTypography>
                                                <MDTypography variant="caption" color="text">
                                                    Address fields are displayed according to the selected service level.
                                                </MDTypography>
                                            </MDBox>

                                            <Grid container spacing={2}>
                                                {showPickupAddress && (
                                                    <Grid item xs={12} md={showDeliveryAddress ? 6 : 12}>
                                                        <MDBox
                                                            p={2}
                                                            sx={{
                                                                height: "100%",
                                                                borderRadius: 2,
                                                                border: "1px solid #e2e8f0",
                                                                backgroundColor: "#ffffff",
                                                            }}
                                                        >
                                                            <MDTypography variant="button" fontWeight="bold">
                                                                Pickup Address
                                                            </MDTypography>

                                                            <Grid container spacing={1.5} mt={0.5}>
                                                                <Grid item xs={12} md={6}>
                                                                    <Autocomplete
                                                                        options={countryOptions}
                                                                        value={
                                                                            countryOptions.find(
                                                                                (option) => option.label === pickupAddress.country
                                                                            ) || null
                                                                        }
                                                                        getOptionLabel={(option) => option?.label || ""}
                                                                        isOptionEqualToValue={(option, value) =>
                                                                            option?.label === value?.label
                                                                        }
                                                                        onChange={(_, value) =>
                                                                            updatePickupAddress("country", value?.label || "")
                                                                        }
                                                                        renderInput={(params) => (
                                                                            <TextField
                                                                                {...params}
                                                                                label="Pickup Country"
                                                                                size="small"
                                                                                fullWidth
                                                                                InputLabelProps={{ shrink: true }}
                                                                            />
                                                                        )}
                                                                    />
                                                                </Grid>

                                                                <Grid item xs={12} md={6}>
                                                                    <Autocomplete
                                                                        freeSolo
                                                                        disabled={!pickupAddress.country}
                                                                        options={pickupCityOptions}
                                                                        loading={pickupCitiesLoading}
                                                                        value={pickupAddress.city || null}
                                                                        inputValue={pickupAddress.city || ""}
                                                                        onInputChange={(_, value, reason) => {
                                                                            if (reason === "input" || reason === "clear") {
                                                                                updatePickupAddress("city", value);
                                                                            }
                                                                        }}
                                                                        onChange={(_, value) =>
                                                                            updatePickupAddress(
                                                                                "city",
                                                                                typeof value === "string" ? value : value || ""
                                                                            )
                                                                        }
                                                                        renderInput={(params) => (
                                                                            <TextField
                                                                                {...params}
                                                                                label="Pickup City"
                                                                                size="small"
                                                                                fullWidth
                                                                                InputLabelProps={{ shrink: true }}
                                                                            />
                                                                        )}
                                                                    />
                                                                </Grid>

                                                                <Grid item xs={12}>
                                                                    <TextField
                                                                        label="Pickup Full Address"
                                                                        value={pickupAddress.additionalInfo || ""}
                                                                        onChange={(event) =>
                                                                            updatePickupAddress("additionalInfo", event.target.value)
                                                                        }
                                                                        multiline
                                                                        minRows={3}
                                                                        fullWidth
                                                                        InputLabelProps={{ shrink: true }}
                                                                    />
                                                                </Grid>

                                                                <Grid item xs={12}>
                                                                    <Autocomplete
                                                                        options={serviceProviderOptions}
                                                                        value={pickupService.serviceProvider || null}
                                                                        onChange={(_, value) =>
                                                                            updatePickupService("serviceProvider", value || "")
                                                                        }
                                                                        getOptionLabel={(option) => String(option || "")}
                                                                        renderInput={(params) => (
                                                                            <TextField
                                                                                {...params}
                                                                                label="Pickup Service Provider"
                                                                                size="small"
                                                                                fullWidth
                                                                                InputLabelProps={{ shrink: true }}
                                                                            />
                                                                        )}
                                                                    />
                                                                </Grid>

                                                                <Grid item xs={12} md={4}>
                                                                    <TextField
                                                                        type="number"
                                                                        label="Pickup Cost"
                                                                        value={pickupService.cost}
                                                                        onChange={(e) => updatePickupService("cost", e.target.value)}
                                                                        size="small"
                                                                        fullWidth
                                                                        inputProps={{ min: 0, step: "0.01" }}
                                                                        InputLabelProps={{ shrink: true }}
                                                                    />
                                                                </Grid>
                                                                <Grid item xs={12} md={2}>
                                                                    <TextField
                                                                        select
                                                                        label="Cost Cur."
                                                                        value={pickupService.costCurrency}
                                                                        onChange={(e) => updatePickupService("costCurrency", e.target.value)}
                                                                        size="small"
                                                                        fullWidth
                                                                        InputLabelProps={{ shrink: true }}
                                                                    >
                                                                        {["USD", "EUR", "TRY", "GBP"].map((code) => (
                                                                            <MenuItem key={code} value={code}>{code}</MenuItem>
                                                                        ))}
                                                                    </TextField>
                                                                </Grid>
                                                                <Grid item xs={12} md={6}>
                                                                    <FormControlLabel
                                                                        control={
                                                                            <Switch
                                                                                checked={pickupService.sellingIncluded}
                                                                                onChange={(e) =>
                                                                                    updatePickupService("sellingIncluded", e.target.checked)
                                                                                }
                                                                            />
                                                                        }
                                                                        label="Selling Included"
                                                                    />
                                                                </Grid>
                                                                <Grid item xs={12} md={4}>
                                                                    <TextField
                                                                        type="number"
                                                                        label="Pickup Selling"
                                                                        value={pickupService.sellingIncluded ? "" : pickupService.selling}
                                                                        onChange={(e) => updatePickupService("selling", e.target.value)}
                                                                        disabled={pickupService.sellingIncluded}
                                                                        helperText={pickupService.sellingIncluded ? "Included in main ocean offer" : ""}
                                                                        size="small"
                                                                        fullWidth
                                                                        inputProps={{ min: 0, step: "0.01" }}
                                                                        InputLabelProps={{ shrink: true }}
                                                                    />
                                                                </Grid>
                                                                <Grid item xs={12} md={2}>
                                                                    <TextField
                                                                        select
                                                                        label="Selling Cur."
                                                                        value={pickupService.sellingCurrency}
                                                                        onChange={(e) => updatePickupService("sellingCurrency", e.target.value)}
                                                                        disabled={pickupService.sellingIncluded}
                                                                        size="small"
                                                                        fullWidth
                                                                        InputLabelProps={{ shrink: true }}
                                                                    >
                                                                        {["USD", "EUR", "TRY", "GBP"].map((code) => (
                                                                            <MenuItem key={code} value={code}>{code}</MenuItem>
                                                                        ))}
                                                                    </TextField>
                                                                </Grid>
                                                            </Grid>
                                                        </MDBox>
                                                    </Grid>
                                                )}

                                                {showDeliveryAddress && (
                                                    <Grid item xs={12} md={showPickupAddress ? 6 : 12}>
                                                        <MDBox
                                                            p={2}
                                                            sx={{
                                                                height: "100%",
                                                                borderRadius: 2,
                                                                border: "1px solid #dcfce7",
                                                                backgroundColor: "#ffffff",
                                                            }}
                                                        >
                                                            <MDTypography variant="button" fontWeight="bold">
                                                                Delivery Address
                                                            </MDTypography>

                                                            <Grid container spacing={1.5} mt={0.5}>
                                                                <Grid item xs={12} md={6}>
                                                                    <Autocomplete
                                                                        options={countryOptions}
                                                                        value={
                                                                            countryOptions.find(
                                                                                (option) => option.label === deliveryAddress.country
                                                                            ) || null
                                                                        }
                                                                        getOptionLabel={(option) => option?.label || ""}
                                                                        isOptionEqualToValue={(option, value) =>
                                                                            option?.label === value?.label
                                                                        }
                                                                        onChange={(_, value) =>
                                                                            updateDeliveryAddress("country", value?.label || "")
                                                                        }
                                                                        renderInput={(params) => (
                                                                            <TextField
                                                                                {...params}
                                                                                label="Delivery Country"
                                                                                size="small"
                                                                                fullWidth
                                                                                InputLabelProps={{ shrink: true }}
                                                                            />
                                                                        )}
                                                                    />
                                                                </Grid>

                                                                <Grid item xs={12} md={6}>
                                                                    <Autocomplete
                                                                        freeSolo
                                                                        disabled={!deliveryAddress.country}
                                                                        options={deliveryCityOptions}
                                                                        loading={deliveryCitiesLoading}
                                                                        value={deliveryAddress.city || null}
                                                                        inputValue={deliveryAddress.city || ""}
                                                                        onInputChange={(_, value, reason) => {
                                                                            if (reason === "input" || reason === "clear") {
                                                                                updateDeliveryAddress("city", value);
                                                                            }
                                                                        }}
                                                                        onChange={(_, value) =>
                                                                            updateDeliveryAddress(
                                                                                "city",
                                                                                typeof value === "string" ? value : value || ""
                                                                            )
                                                                        }
                                                                        renderInput={(params) => (
                                                                            <TextField
                                                                                {...params}
                                                                                label="Delivery City"
                                                                                size="small"
                                                                                fullWidth
                                                                                InputLabelProps={{ shrink: true }}
                                                                            />
                                                                        )}
                                                                    />
                                                                </Grid>

                                                                <Grid item xs={12}>
                                                                    <TextField
                                                                        label="Delivery Full Address"
                                                                        value={deliveryAddress.additionalInfo || ""}
                                                                        onChange={(event) =>
                                                                            updateDeliveryAddress("additionalInfo", event.target.value)
                                                                        }
                                                                        multiline
                                                                        minRows={3}
                                                                        fullWidth
                                                                        InputLabelProps={{ shrink: true }}
                                                                    />
                                                                </Grid>

                                                                <Grid item xs={12}>
                                                                    <Autocomplete
                                                                        options={serviceProviderOptions}
                                                                        value={deliveryService.serviceProvider || null}
                                                                        onChange={(_, value) =>
                                                                            updateDeliveryService("serviceProvider", value || "")
                                                                        }
                                                                        getOptionLabel={(option) => String(option || "")}
                                                                        renderInput={(params) => (
                                                                            <TextField
                                                                                {...params}
                                                                                label="Delivery Service Provider"
                                                                                size="small"
                                                                                fullWidth
                                                                                InputLabelProps={{ shrink: true }}
                                                                            />
                                                                        )}
                                                                    />
                                                                </Grid>

                                                                <Grid item xs={12} md={4}>
                                                                    <TextField
                                                                        type="number"
                                                                        label="Delivery Cost"
                                                                        value={deliveryService.cost}
                                                                        onChange={(e) => updateDeliveryService("cost", e.target.value)}
                                                                        size="small"
                                                                        fullWidth
                                                                        inputProps={{ min: 0, step: "0.01" }}
                                                                        InputLabelProps={{ shrink: true }}
                                                                    />
                                                                </Grid>
                                                                <Grid item xs={12} md={2}>
                                                                    <TextField
                                                                        select
                                                                        label="Cost Cur."
                                                                        value={deliveryService.costCurrency}
                                                                        onChange={(e) => updateDeliveryService("costCurrency", e.target.value)}
                                                                        size="small"
                                                                        fullWidth
                                                                        InputLabelProps={{ shrink: true }}
                                                                    >
                                                                        {["USD", "EUR", "TRY", "GBP"].map((code) => (
                                                                            <MenuItem key={code} value={code}>{code}</MenuItem>
                                                                        ))}
                                                                    </TextField>
                                                                </Grid>
                                                                <Grid item xs={12} md={6}>
                                                                    <FormControlLabel
                                                                        control={
                                                                            <Switch
                                                                                checked={deliveryService.sellingIncluded}
                                                                                onChange={(e) =>
                                                                                    updateDeliveryService("sellingIncluded", e.target.checked)
                                                                                }
                                                                            />
                                                                        }
                                                                        label="Selling Included"
                                                                    />
                                                                </Grid>
                                                                <Grid item xs={12} md={4}>
                                                                    <TextField
                                                                        type="number"
                                                                        label="Delivery Selling"
                                                                        value={deliveryService.sellingIncluded ? "" : deliveryService.selling}
                                                                        onChange={(e) => updateDeliveryService("selling", e.target.value)}
                                                                        disabled={deliveryService.sellingIncluded}
                                                                        helperText={deliveryService.sellingIncluded ? "Included in main ocean offer" : ""}
                                                                        size="small"
                                                                        fullWidth
                                                                        inputProps={{ min: 0, step: "0.01" }}
                                                                        InputLabelProps={{ shrink: true }}
                                                                    />
                                                                </Grid>
                                                                <Grid item xs={12} md={2}>
                                                                    <TextField
                                                                        select
                                                                        label="Selling Cur."
                                                                        value={deliveryService.sellingCurrency}
                                                                        onChange={(e) => updateDeliveryService("sellingCurrency", e.target.value)}
                                                                        disabled={deliveryService.sellingIncluded}
                                                                        size="small"
                                                                        fullWidth
                                                                        InputLabelProps={{ shrink: true }}
                                                                    >
                                                                        {["USD", "EUR", "TRY", "GBP"].map((code) => (
                                                                            <MenuItem key={code} value={code}>{code}</MenuItem>
                                                                        ))}
                                                                    </TextField>
                                                                </Grid>
                                                            </Grid>
                                                        </MDBox>
                                                    </Grid>
                                                )}
                                            </Grid>
                                        </MDBox>
                                    )}

                                    <MDBox mt={3}>
                                        <Box display="flex" justifyContent="space-between" alignItems="baseline" mb={1.5} flexWrap="wrap" gap={1}>
                                            <MDTypography variant="h6" fontWeight="bold">
                                                Route
                                            </MDTypography>
                                            <MDTypography variant="caption" color="text">
                                                Port of loading and discharge apply to all carriers below.
                                            </MDTypography>
                                        </Box>
                                        {routesDiffer && (
                                            <MDTypography variant="caption" display="block" sx={{ color: "#b45309", mb: 1 }}>
                                                This quote was saved with different ports per carrier. The route below is taken from Carrier #1; changing it applies the new route to all carriers.
                                            </MDTypography>
                                        )}
                                        <MDBox
                                            sx={{
                                                p: 2,
                                                borderRadius: 2,
                                                border: "1px solid #e5e7eb",
                                                backgroundColor: "#fff",
                                                boxShadow: "none",
                                            }}
                                        >
                                            <Grid container spacing={1.5}>
                                                <Grid item xs={12} sm={6} md={3}>
                                                    <Autocomplete
                                                        options={countryOptions}
                                                        value={route.polCountry || null}
                                                        getOptionLabel={(o) => o?.label || ""}
                                                        isOptionEqualToValue={(opt, val) =>
                                                            (opt?.label || "").trim().toLowerCase() ===
                                                            (val?.label || "").trim().toLowerCase()
                                                        }
                                                        onChange={(_, v) =>
                                                            handleRouteChange({ polCountry: v, pol: "", polInput: "", polOptions: [], polLoading: false })
                                                        }
                                                        renderInput={(params) => (
                                                            <TextField {...params} label="POL Country" size="small" fullWidth required InputLabelProps={{ shrink: true }} />
                                                        )}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6} md={3}>
                                                    <Autocomplete
                                                        freeSolo
                                                        disabled={!route.polCountry}
                                                        options={route.polOptions || []}
                                                        loading={route.polLoading}
                                                        value={route.pol ? { label: route.pol } : null}
                                                        isOptionEqualToValue={(o, val) => (o?.label || "") === (val?.label || "")}
                                                        inputValue={route.polInput ?? route.pol ?? ""}
                                                        onInputChange={(_, val, reason) => {
                                                            if (reason === "input") {
                                                                handleRouteChange({ polInput: val, pol: val });
                                                            } else {
                                                                handleRouteChange({ polInput: val });
                                                            }
                                                        }}
                                                        onChange={(_, v) => {
                                                            const next = (typeof v === "string" ? v : v?.label) || "";
                                                            handleRouteChange({ pol: next, polInput: next });
                                                        }}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                label="Port of Loading (POL)"
                                                                size="small"
                                                                fullWidth
                                                                required
                                                                InputLabelProps={{ shrink: true }}
                                                                placeholder={route.polCountry ? "Search port" : "Select country first"}
                                                            />
                                                        )}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6} md={3}>
                                                    <Autocomplete
                                                        options={countryOptions}
                                                        value={route.podCountry || null}
                                                        getOptionLabel={(o) => o?.label || ""}
                                                        isOptionEqualToValue={(opt, val) =>
                                                            (opt?.label || "").trim().toLowerCase() ===
                                                            (val?.label || "").trim().toLowerCase()
                                                        }
                                                        onChange={(_, v) =>
                                                            handleRouteChange({ podCountry: v, pod: "", podInput: "", podOptions: [], podLoading: false })
                                                        }
                                                        renderInput={(params) => (
                                                            <TextField {...params} label="POD Country" size="small" fullWidth required InputLabelProps={{ shrink: true }} />
                                                        )}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6} md={3}>
                                                    <Autocomplete
                                                        freeSolo
                                                        disabled={!route.podCountry}
                                                        options={route.podOptions || []}
                                                        loading={route.podLoading}
                                                        value={route.pod ? { label: route.pod } : null}
                                                        isOptionEqualToValue={(o, val) => (o?.label || "") === (val?.label || "")}
                                                        inputValue={route.podInput ?? route.pod ?? ""}
                                                        onInputChange={(_, val, reason) => {
                                                            if (reason === "input") {
                                                                handleRouteChange({ podInput: val, pod: val });
                                                            } else {
                                                                handleRouteChange({ podInput: val });
                                                            }
                                                        }}
                                                        onChange={(_, v) => {
                                                            const next = (typeof v === "string" ? v : v?.label) || "";
                                                            handleRouteChange({ pod: next, podInput: next });
                                                        }}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                label="Port of Discharge (POD)"
                                                                size="small"
                                                                fullWidth
                                                                required
                                                                InputLabelProps={{ shrink: true }}
                                                                placeholder={route.podCountry ? "Search port" : "Select country first"}
                                                            />
                                                        )}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </MDBox>
                                    </MDBox>

                                    <MDBox mt={3}>
                                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                                            <MDTypography variant="h6" fontWeight="bold">
                                                Carriers & Freight Costs
                                            </MDTypography>
                                        </Box>

                                        {carriers.map((row, index) => (
                                            <MDBox
                                                key={index}
                                                sx={{
                                                    mb: 2,
                                                    p: 2,
                                                    borderRadius: 2,
                                                    border: "1px solid #e5e7eb",
                                                    backgroundColor: "#fff",
                                                    boxShadow: "none",
                                                }}
                                            >
                                                <Box
                                                    display="flex"
                                                    justifyContent="space-between"
                                                    alignItems="center"
                                                    mb={1.5}
                                                >
                                                    <MDTypography variant="button" fontWeight="bold" color="text">
                                                        Carrier #{index + 1}
                                                    </MDTypography>

                                                    <Box display="flex" gap={1}>
                                                        {index === carriers.length - 1 && (
                                                            <AddRowButton onClick={addCarrier} tooltip="Add carrier" />
                                                        )}

                                                        {index > 0 && (
                                                            <RemoveRowButton onClick={() => removeCarrier(index)} tooltip="Remove carrier" />
                                                        )}
                                                    </Box>
                                                </Box>

                                                <Grid container spacing={1.5}>
                                                    <Grid item xs={12} md={2}>
                                                        <Autocomplete
                                                            options={DEFAULT_LINE_OPTIONS}
                                                            value={DEFAULT_LINE_OPTIONS.find((item) => item.name === row.name) || null}
                                                            onChange={(event, newValue) =>
                                                                handleCarrierChange(index, "name", newValue?.name || "")
                                                            }
                                                            getOptionLabel={(option) => option?.name || ""}
                                                            isOptionEqualToValue={(option, value) => option.id === value.id}
                                                            renderInput={(params) => (
                                                                <TextField
                                                                    {...params}
                                                                    label="Carrier"
                                                                    size="small"
                                                                    fullWidth
                                                                    InputLabelProps={{ shrink: true }}
                                                                />
                                                            )}
                                                        />
                                                    </Grid>

                                                    <Grid item xs={12} md={2}>
                                                        <Autocomplete
                                                            options={chargeTypeOptions}
                                                            getOptionLabel={(option) => option.label}
                                                            value={chargeTypeOptions.find((o) => o.label === row.chargeType) || null}
                                                            onChange={(_, newValue) =>
                                                                handleCarrierChange(index, "chargeType", newValue?.label || "")
                                                            }
                                                            renderInput={(params) => (
                                                                <TextField
                                                                    {...params}
                                                                    label="Charge Type"
                                                                    size="small"
                                                                    fullWidth
                                                                    InputLabelProps={{ shrink: true }}
                                                                />
                                                            )}
                                                        />
                                                    </Grid>

                                                    <Grid item xs={12} md={2}>
                                                        <LocalizationProvider dateAdapter={AdapterDateFns} locale={tr}>
                                                            <DatePicker
                                                                label="Close Date"
                                                                value={row.estimatedCloseDate || null}
                                                                onChange={(newValue) =>
                                                                    handleCarrierChange(index, "estimatedCloseDate", newValue)
                                                                }
                                                                format="dd/MM/yyyy"
                                                                slotProps={{
                                                                    textField: {
                                                                        fullWidth: true,
                                                                        size: "small",
                                                                        InputLabelProps: { shrink: true },
                                                                    },
                                                                }}
                                                            />
                                                        </LocalizationProvider>
                                                    </Grid>

                                                    <Grid item xs={12} md={2}>
                                                        <TextField
                                                            label="Transit Days"
                                                            value={row.transitTime || ""}
                                                            onChange={(e) =>
                                                                handleCarrierChange(index, "transitTime", e.target.value)
                                                            }
                                                            size="small"
                                                            fullWidth
                                                            InputLabelProps={{ shrink: true }}
                                                        />
                                                    </Grid>

                                                    <Grid item xs={12} md={2}>
                                                        <TextField
                                                            label="Free Time"
                                                            value={row.originFreeTime || ""}
                                                            onChange={(e) =>
                                                                handleCarrierChange(index, "originFreeTime", e.target.value)
                                                            }
                                                            size="small"
                                                            fullWidth
                                                            InputLabelProps={{ shrink: true }}
                                                        />
                                                    </Grid>

                                                    <Grid item xs={12} md={2}>
                                                        <Autocomplete
                                                            options={containerTypes}
                                                            getOptionLabel={(o) => o.label}
                                                            value={row.containerTypes ? { label: row.containerTypes } : null}
                                                            onChange={(_, v) =>
                                                                handleCarrierChange(index, "containerTypes", v?.label || "")
                                                            }
                                                            isOptionEqualToValue={(o, v) => o.label === v.label}
                                                            renderInput={(params) => (
                                                                <TextField
                                                                    {...params}
                                                                    label="Container"
                                                                    size="small"
                                                                    fullWidth
                                                                    InputLabelProps={{ shrink: true }}
                                                                />
                                                            )}
                                                        />
                                                    </Grid>

                                                    <Grid item xs={6} md={1}>
                                                        <TextField
                                                            type="number"
                                                            label="PCS"
                                                            value={row.containerPcs}
                                                            onChange={(e) =>
                                                                handleCarrierChange(index, "containerPcs", e.target.value)
                                                            }
                                                            size="small"
                                                            fullWidth
                                                            InputLabelProps={{ shrink: true }}
                                                        />
                                                    </Grid>

                                                    <Grid item xs={6} md={1}>
                                                        <TextField
                                                            type="number"
                                                            label="Cost"
                                                            value={row.cost ?? ""}
                                                            onChange={(e) => handleCarrierChange(index, "cost", e.target.value)}
                                                            size="small"
                                                            fullWidth
                                                            InputLabelProps={{ shrink: true }}
                                                        />
                                                    </Grid>

                                                    <Grid item xs={6} md={1}>
                                                        <TextField
                                                            select
                                                            label="Cur."
                                                            value={row.currency}
                                                            onChange={(e) =>
                                                                handleCarrierChange(index, "currency", e.target.value)
                                                            }
                                                            size="small"
                                                            fullWidth
                                                            InputLabelProps={{ shrink: true }}
                                                        >
                                                            <MenuItem value="USD">USD</MenuItem>
                                                            <MenuItem value="EUR">EUR</MenuItem>
                                                            <MenuItem value="TRY">TRY</MenuItem>
                                                            <MenuItem value="GBP">GBP</MenuItem>
                                                        </TextField>
                                                    </Grid>

                                                    <Grid item xs={6} md={1.5}>
                                                        <TextField
                                                            type="number"
                                                            label="Selling"
                                                            value={row.sellingCarriers ?? ""}
                                                            onChange={(e) =>
                                                                handleCarrierChange(index, "sellingCarriers", e.target.value)
                                                            }
                                                            size="small"
                                                            fullWidth
                                                            InputLabelProps={{ shrink: true }}
                                                        />
                                                    </Grid>

                                                    <Grid item xs={6} md={1}>
                                                        <TextField
                                                            select
                                                            label="Cur."
                                                            value={row.sellingCarriersRate}
                                                            onChange={(e) =>
                                                                handleCarrierChange(index, "sellingCarriersRate", e.target.value)
                                                            }
                                                            size="small"
                                                            fullWidth
                                                            InputLabelProps={{ shrink: true }}
                                                        >
                                                            <MenuItem value="USD">USD</MenuItem>
                                                            <MenuItem value="EUR">EUR</MenuItem>
                                                            <MenuItem value="TRY">TRY</MenuItem>
                                                            <MenuItem value="GBP">GBP</MenuItem>
                                                        </TextField>
                                                    </Grid>
                                                </Grid>
                                            </MDBox>
                                        ))}
                                    </MDBox>

                                    <MDBox mt={3}>
                                        <MDBox mt={3}>
                                            <MDTypography variant="h6">Cargo Details</MDTypography>
                                        </MDBox>
                                        <Grid container spacing={3} mt={1}>
                                            <Grid item xs={12} sm={2}>
                                                <TextField
                                                    type="text"
                                                    label="HS Code"
                                                    value={hsCode}
                                                    fullWidth
                                                    InputLabelProps={{ shrink: true }}
                                                    sx={{ "& .MuiInputBase-root": { height: 40 } }}
                                                    onChange={(e) => setHsCode(e.target.value)}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={2}>
                                                <TextField
                                                    type="number"
                                                    label="Gross Weight (kg)"
                                                    value={grossWeight}
                                                    fullWidth
                                                    InputLabelProps={{ shrink: true }}
                                                    sx={{ "& .MuiInputBase-root": { height: 40 } }}
                                                    onChange={(e) => setGrossWeight(e.target.value)}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={2}>
                                                <TextField
                                                    type="number"
                                                    label="Volume (m3)"
                                                    value={volume}
                                                    fullWidth
                                                    InputLabelProps={{ shrink: true }}
                                                    sx={{ "& .MuiInputBase-root": { height: 40 } }}
                                                    onChange={(e) => setVolume(e.target.value)}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={2}>
                                                <TextField
                                                    type="number"
                                                    label="Quantity"
                                                    value={quantity}
                                                    fullWidth
                                                    InputLabelProps={{ shrink: true }}
                                                    sx={{ "& .MuiInputBase-root": { height: 40 } }}
                                                    onChange={(e) => setQuantity(e.target.value)}
                                                />
                                            </Grid>

                                        </Grid>
                                        <Grid container spacing={3} mt={1}>
                                            <Grid item xs={12}>
                                                <TextField
                                                    type="text"
                                                    label="Cargo Description"
                                                    value={cargoDescription}
                                                    fullWidth
                                                    InputLabelProps={{ shrink: true }}
                                                    sx={{ "& .MuiInputBase-root": { height: 40 } }}
                                                    onChange={(e) => setCargoDescription(e.target.value)}
                                                />
                                            </Grid>
                                        </Grid>
                                    </MDBox>
                                    <MDBox mt={3} display="flex" gap={4}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={oceanPricingMode === "ALL_IN"}
                                                    onChange={() => setOceanPricingMode("ALL_IN")}
                                                />
                                            }
                                            label="All-in"
                                        />
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={oceanPricingMode === "BREAKDOWN"}
                                                    onChange={() => setOceanPricingMode("BREAKDOWN")}
                                                />
                                            }
                                            label="Breakdown"
                                        />
                                    </MDBox>

                                    <MDTypography variant="h6">Extra Charges</MDTypography>
                                    {extraCosts.map((row, index) => (
                                        <Grid container spacing={2} key={index} mt={1} alignItems="center">

                                            <Grid item xs={3}>
                                                <Autocomplete
                                                    options={
                                                        // kayıtlı tip güncel listede yoksa da görünsün; boş değer null olmalı
                                                        row.type && !extraChargeOptions.includes(row.type)
                                                            ? [...extraChargeOptions, row.type]
                                                            : extraChargeOptions
                                                    }
                                                    value={row.type || null}
                                                    onChange={(_, v) =>
                                                        handleExtraChange(index, "type", v || "")
                                                    }
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            label="Charge Type"
                                                            fullWidth
                                                            sx={{ "& .MuiInputBase-root": { height: 50 } }}
                                                        />
                                                    )}
                                                />

                                            </Grid>

                                            <Grid item xs={1}>
                                                <TextField
                                                    type="number"
                                                    label="Cost"
                                                    value={row.amount ?? ""}
                                                    onChange={(e) => handleExtraChange(index, "amount", e.target.value)}
                                                    fullWidth
                                                    sx={{ "& .MuiInputBase-root": { height: 40 } }}
                                                />
                                            </Grid>

                                            <Grid item xs={1}>
                                                <TextField
                                                    select
                                                    value={row.currency}
                                                    onChange={(e) => handleExtraChange(index, "currency", e.target.value)}
                                                    fullWidth
                                                >
                                                    <MenuItem value="USD">USD</MenuItem>
                                                    <MenuItem value="EUR">EUR</MenuItem>
                                                    <MenuItem value="TRY">TRY</MenuItem>
                                                    <MenuItem value="GBP">GBP</MenuItem>
                                                </TextField>
                                            </Grid>

                                            <Grid item xs={2}>
                                                <TextField
                                                    type="number"
                                                    label="Selling Rate"
                                                    value={row.extraSelling ?? ""}
                                                    onChange={(e) => handleExtraChange(index, "extraSelling", e.target.value)}
                                                    fullWidth
                                                    sx={{ "& .MuiInputBase-root": { height: 40 } }}
                                                />
                                            </Grid>

                                            <Grid item xs={1}>
                                                <TextField
                                                    select
                                                    value={row.extraSellingRate}
                                                    onChange={(e) =>
                                                        handleExtraChange(index, "extraSellingRate", e.target.value)
                                                    }
                                                    fullWidth
                                                >
                                                    <MenuItem value="USD">USD</MenuItem>
                                                    <MenuItem value="EUR">EUR</MenuItem>
                                                    <MenuItem value="TRY">TRY</MenuItem>
                                                    <MenuItem value="GBP">GBP</MenuItem>
                                                </TextField>
                                            </Grid>

                                            <Grid item xs={1} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                {index === extraCosts.length - 1 && (
                                                    <AddRowButton onClick={addExtra} tooltip="Add charge" />
                                                )}

                                                {index > 0 && (
                                                    <RemoveRowButton onClick={() => removeExtra(index)} tooltip="Remove charge" />
                                                )}
                                            </Grid>

                                        </Grid>
                                    ))}

                                    {/* TOTAL CARD */}
                                    {/* BUYING / SELLING SUMMARY CARD */}
                                    <MDBox
                                        mt={4}
                                        p={2.5}
                                        sx={{
                                            borderRadius: 2,
                                            backgroundColor: "#ffffff",
                                            border: "1px solid #e5e7eb",
                                            boxShadow: "none",
                                        }}
                                    >
                                        <Box
                                            display="flex"
                                            justifyContent="space-between"
                                            alignItems="center"
                                            mb={2}
                                            gap={2}
                                        >
                                            <Box>
                                                <MDTypography
                                                    variant="h6"
                                                    fontWeight="bold"
                                                >
                                                    Buying / Selling Summary
                                                </MDTypography>

                                                <MDTypography
                                                    variant="caption"
                                                    color="text"
                                                    sx={{ opacity: 0.7 }}
                                                >
                                                    Line items retain their selected currencies.
                                                    Totals are converted to the carrier currency.
                                                </MDTypography>
                                            </Box>

                                            {loadingRates && (
                                                <MDTypography
                                                    variant="caption"
                                                    sx={{
                                                        color: "#ed6c02",
                                                        whiteSpace: "nowrap",
                                                    }}
                                                >
                                                    Loading exchange rates...
                                                </MDTypography>
                                            )}
                                        </Box>

                                        {(carriers || []).map(
                                            (carrierRow, carrierIndex) => {
                                                const isBreakdown =
                                                    String(
                                                        oceanPricingMode || ""
                                                    ).toUpperCase() === "BREAKDOWN";

                                                /*
                                                 * BUYING TARGET CURRENCY
                                                 *
                                                 * Carrier Cost alanının yanında seçilen currency.
                                                 */
                                                const buyingCurrency =
                                                    normalizeSummaryCurrency(
                                                        carrierRow.currency,
                                                        baseCurrency
                                                    );

                                                /*
                                                 * SELLING TARGET CURRENCY
                                                 *
                                                 * BREAKDOWN'da Carrier Selling alanının
                                                 * yanında seçilen currency.
                                                 *
                                                 * ALL-IN'da teklifin genel base currency'si.
                                                 */
                                                const sellingCurrency =
                                                    normalizeSummaryCurrency(
                                                        carrierRow.sellingCarriersRate,
                                                        baseCurrency
                                                    );

                                                /*
                                                 * Formdaki orijinal carrier değerleri.
                                                 * Detay satırlarında dönüşüm yapılmadan gösterilir.
                                                 */
                                                const carrierBuyingAmount =
                                                    toSummaryNumber(
                                                        carrierRow.cost
                                                    );

                                                const carrierSellingAmount =
                                                    toSummaryNumber(
                                                        carrierRow.sellingCarriers
                                                    );

                                                /*
                                                 * EXTRA BUYING
                                                 *
                                                 * originalAmount ve originalCurrency:
                                                 * Kartta gösterilecek kayıtlı değerler.
                                                 *
                                                 * convertedAmount:
                                                 * Yalnızca Buying Total hesabında kullanılacak.
                                                 */
                                                const extraBuyingRows = (
                                                    extraCosts || []
                                                )
                                                    .filter(
                                                        (extra) =>
                                                            extra?.type ||
                                                            toSummaryNumber(
                                                                extra?.amount
                                                            ) !== 0
                                                    )
                                                    .map(
                                                        (
                                                            extra,
                                                            extraIndex
                                                        ) => {
                                                            const originalAmount =
                                                                toSummaryNumber(
                                                                    extra.amount
                                                                );

                                                            const originalCurrency =
                                                                normalizeSummaryCurrency(
                                                                    extra.currency,
                                                                    buyingCurrency
                                                                );

                                                            const convertedAmount =
                                                                convertSummaryAmount(
                                                                    originalAmount,
                                                                    originalCurrency,
                                                                    buyingCurrency
                                                                );

                                                            return {
                                                                key: `buying-${carrierIndex}-${extraIndex}`,
                                                                label:
                                                                    extra.type ||
                                                                    `Extra Cost #${
                                                                        extraIndex +
                                                                        1
                                                                    }`,
                                                                originalAmount,
                                                                originalCurrency,
                                                                convertedAmount,
                                                            };
                                                        }
                                                    );

                                                /*
                                                 * EXTRA SELLING
                                                 *
                                                 * Breakdown satırları formdaki extraSelling ve
                                                 * extraSellingRate değerlerini aynen gösterir.
                                                 *
                                                 * convertedAmount yalnızca Selling Total
                                                 * hesabında kullanılır.
                                                 */
                                                const extraSellingRows = (
                                                    extraCosts || []
                                                )
                                                    .filter(
                                                        (extra) =>
                                                            extra?.type ||
                                                            toSummaryNumber(
                                                                extra?.extraSelling
                                                            ) !== 0
                                                    )
                                                    .map((extra, extraIndex) => {
                                                        const originalAmount =
                                                            toSummaryNumber(
                                                                extra.extraSelling
                                                            );

                                                        const originalCurrency =
                                                            normalizeSummaryCurrency(
                                                                extra.extraSellingRate,
                                                                sellingCurrency
                                                            );

                                                        const convertedAmount =
                                                            convertSummaryAmount(
                                                                originalAmount,
                                                                originalCurrency,
                                                                sellingCurrency
                                                            );

                                                        return {
                                                            key: `selling-${carrierIndex}-${extraIndex}`,
                                                            label:
                                                                extra.type ||
                                                                `Extra Selling #${extraIndex + 1}`,
                                                            originalAmount,
                                                            originalCurrency,
                                                            convertedAmount,
                                                        };
                                                    });

                                                const pickupBuyingRow = showPickupAddress
                                                    ? {
                                                        key: `pickup-buying-${carrierIndex}`,
                                                        label: pickupService.serviceProvider
                                                            ? `Pickup (${pickupService.serviceProvider})`
                                                            : "Pickup Service",
                                                        originalAmount: toSummaryNumber(pickupService.cost),
                                                        originalCurrency: normalizeSummaryCurrency(
                                                            pickupService.costCurrency,
                                                            buyingCurrency
                                                        ),
                                                        convertedAmount: convertSummaryAmount(
                                                            pickupService.cost,
                                                            pickupService.costCurrency,
                                                            buyingCurrency
                                                        ),
                                                    }
                                                    : null;

                                                const deliveryBuyingRow = showDeliveryAddress
                                                    ? {
                                                        key: `delivery-buying-${carrierIndex}`,
                                                        label: deliveryService.serviceProvider
                                                            ? `Delivery (${deliveryService.serviceProvider})`
                                                            : "Delivery Service",
                                                        originalAmount: toSummaryNumber(deliveryService.cost),
                                                        originalCurrency: normalizeSummaryCurrency(
                                                            deliveryService.costCurrency,
                                                            buyingCurrency
                                                        ),
                                                        convertedAmount: convertSummaryAmount(
                                                            deliveryService.cost,
                                                            deliveryService.costCurrency,
                                                            buyingCurrency
                                                        ),
                                                    }
                                                    : null;

                                                const pickupSellingRow = showPickupAddress
                                                    ? {
                                                        key: `pickup-selling-${carrierIndex}`,
                                                        label: pickupService.serviceProvider
                                                            ? `Pickup (${pickupService.serviceProvider})`
                                                            : "Pickup Service",
                                                        included: Boolean(pickupService.sellingIncluded),
                                                        originalAmount: pickupService.sellingIncluded
                                                            ? 0
                                                            : toSummaryNumber(pickupService.selling),
                                                        originalCurrency: normalizeSummaryCurrency(
                                                            pickupService.sellingCurrency,
                                                            sellingCurrency
                                                        ),
                                                        convertedAmount: pickupService.sellingIncluded
                                                            ? 0
                                                            : convertSummaryAmount(
                                                                pickupService.selling,
                                                                pickupService.sellingCurrency,
                                                                sellingCurrency
                                                            ),
                                                    }
                                                    : null;

                                                const deliverySellingRow = showDeliveryAddress
                                                    ? {
                                                        key: `delivery-selling-${carrierIndex}`,
                                                        label: deliveryService.serviceProvider
                                                            ? `Delivery (${deliveryService.serviceProvider})`
                                                            : "Delivery Service",
                                                        included: Boolean(deliveryService.sellingIncluded),
                                                        originalAmount: deliveryService.sellingIncluded
                                                            ? 0
                                                            : toSummaryNumber(deliveryService.selling),
                                                        originalCurrency: normalizeSummaryCurrency(
                                                            deliveryService.sellingCurrency,
                                                            sellingCurrency
                                                        ),
                                                        convertedAmount: deliveryService.sellingIncluded
                                                            ? 0
                                                            : convertSummaryAmount(
                                                                deliveryService.selling,
                                                                deliveryService.sellingCurrency,
                                                                sellingCurrency
                                                            ),
                                                    }
                                                    : null;

                                                const serviceBuyingRows = [pickupBuyingRow, deliveryBuyingRow].filter(Boolean);
                                                const serviceSellingRows = [pickupSellingRow, deliverySellingRow].filter(Boolean);

                                                const buyingConversionFailed =
                                                    [...extraBuyingRows, ...serviceBuyingRows].some(
                                                        (item) =>
                                                            item.convertedAmount ===
                                                            null
                                                    );

                                                const sellingConversionFailed =
                                                    [...extraSellingRows, ...serviceSellingRows].some(
                                                        (item) => item.convertedAmount === null
                                                    );

                                                /*
                                                 * Buying Total:
                                                 *
                                                 * Carrier Cost +
                                                 * Extra Cost kalemlerinin carrier cost
                                                 * currency'sine çevrilmiş karşılıkları.
                                                 */
                                                const finalBuying =
                                                    buyingConversionFailed
                                                        ? null
                                                        : carrierBuyingAmount +
                                                        [...extraBuyingRows, ...serviceBuyingRows].reduce(
                                                            (total, item) =>
                                                                total +
                                                                toSummaryNumber(item.convertedAmount),
                                                            0
                                                        );

                                                /*
                                                 * Selling Total:
                                                 *
                                                 * BREAKDOWN:
                                                 * Carrier Selling +
                                                 * Extra Selling kalemlerinin carrier selling
                                                 * currency'sine çevrilmiş karşılıkları.
                                                 *
                                                 * ALL-IN:
                                                 * Mevcut selling engine'in finalSelling değeri.
                                                 */
                                                const additionalSellingTotal =
                                                    sellingConversionFailed
                                                        ? null
                                                        : [...extraSellingRows, ...serviceSellingRows].reduce(
                                                            (total, item) =>
                                                                total +
                                                                toSummaryNumber(item.convertedAmount),
                                                            0
                                                        );

                                                const mainSellingAmount = isBreakdown
                                                    ? carrierSellingAmount
                                                    : carrierSellingAmount > 0
                                                        ? carrierSellingAmount
                                                        : Math.max(
                                                            0,
                                                            toSummaryNumber(sellingCalc.finalSelling) -
                                                            toSummaryNumber(additionalSellingTotal)
                                                        );

                                                const finalSelling = sellingConversionFailed
                                                    ? null
                                                    : mainSellingAmount +
                                                    toSummaryNumber(additionalSellingTotal);

                                                return (
                                                    <MDBox
                                                        key={
                                                            carrierRow.id ||
                                                            carrierIndex
                                                        }
                                                        mb={
                                                            carrierIndex ===
                                                            carriers.length - 1
                                                                ? 0
                                                                : 2
                                                        }
                                                        p={1.75}
                                                        sx={{
                                                            borderRadius: 2,
                                                            backgroundColor:
                                                                "#ffffff",
                                                            border:
                                                                "1px solid #e5e7eb",
                                                            boxShadow:
                                                                "none",
                                                        }}
                                                    >
                                                        {/* CARRIER HEADER */}
                                                        <Box
                                                            display="flex"
                                                            justifyContent="space-between"
                                                            alignItems="center"
                                                            flexWrap="wrap"
                                                            gap={1}
                                                            mb={1.5}
                                                        >
                                                            <Box>
                                                                <MDTypography
                                                                    variant="button"
                                                                    fontWeight="bold"
                                                                    display="block"
                                                                >
                                                                    {carrierRow.name ||
                                                                        `Carrier #${
                                                                            carrierIndex +
                                                                            1
                                                                        }`}
                                                                </MDTypography>

                                                                <MDTypography
                                                                    variant="caption"
                                                                    color="text"
                                                                    sx={{
                                                                        opacity: 0.65,
                                                                    }}
                                                                >
                                                                    {carrierRow.chargeType ||
                                                                        "Ocean Freight Charge"}
                                                                </MDTypography>
                                                            </Box>

                                                            <Box
                                                                display="flex"
                                                                gap={1}
                                                                flexWrap="wrap"
                                                            >
                                                                <Box
                                                                    sx={{
                                                                        px: 1.25,
                                                                        py: 0.5,
                                                                        borderRadius: 1.5,
                                                                        fontSize: 11,
                                                                        fontWeight: 600,
                                                                        color: "#475569",
                                                                        backgroundColor:
                                                                            "#f1f5f9",
                                                                    }}
                                                                >
                                                                    Buying:{" "}
                                                                    {buyingCurrency}
                                                                </Box>

                                                                <Box
                                                                    sx={{
                                                                        px: 1.25,
                                                                        py: 0.5,
                                                                        borderRadius: 1.5,
                                                                        fontSize: 11,
                                                                        fontWeight: 600,
                                                                        color: "#166534",
                                                                        backgroundColor:
                                                                            "#dcfce7",
                                                                    }}
                                                                >
                                                                    Selling:{" "}
                                                                    {sellingCurrency}
                                                                </Box>
                                                            </Box>
                                                        </Box>

                                                        <Grid
                                                            container
                                                            spacing={1.5}
                                                        >
                                                            {/* BUYING CARD */}
                                                            <Grid
                                                                item
                                                                xs={12}
                                                                md={6}
                                                            >
                                                                <MDBox
                                                                    sx={{
                                                                        height: "100%",
                                                                        p: 1.5,
                                                                        borderRadius: 1.5,
                                                                        backgroundColor:
                                                                            "#f8fafc",
                                                                        border:
                                                                            "1px solid #e2e8f0",
                                                                    }}
                                                                >
                                                                    <Box
                                                                        display="flex"
                                                                        justifyContent="space-between"
                                                                        alignItems="flex-start"
                                                                        gap={2}
                                                                        pb={1}
                                                                        mb={1}
                                                                        sx={{
                                                                            borderBottom:
                                                                                "1px solid #e2e8f0",
                                                                        }}
                                                                    >
                                                                        <Box>
                                                                            <MDTypography
                                                                                variant="caption"
                                                                                fontWeight="bold"
                                                                                display="block"
                                                                            >
                                                                                Buying
                                                                                Total
                                                                            </MDTypography>

                                                                            <MDTypography
                                                                                variant="caption"
                                                                                color="text"
                                                                                sx={{
                                                                                    opacity: 0.6,
                                                                                }}
                                                                            >
                                                                                Converted
                                                                                to{" "}
                                                                                {
                                                                                    buyingCurrency
                                                                                }
                                                                            </MDTypography>
                                                                        </Box>

                                                                        <MDTypography
                                                                            variant="caption"
                                                                            fontWeight="bold"
                                                                            textAlign="right"
                                                                            sx={{
                                                                                color:
                                                                                    finalBuying ===
                                                                                    null
                                                                                        ? "#d32f2f"
                                                                                        : "#0f172a",
                                                                            }}
                                                                        >
                                                                            {formatSummaryMoney(
                                                                                finalBuying,
                                                                                buyingCurrency
                                                                            )}
                                                                        </MDTypography>
                                                                    </Box>

                                                                    {/* Carrier Cost: Original */}
                                                                    <Box
                                                                        display="flex"
                                                                        justifyContent="space-between"
                                                                        alignItems="center"
                                                                        gap={2}
                                                                        mb={0.75}
                                                                    >
                                                                        <MDTypography variant="caption">
                                                                            Carrier
                                                                            Cost
                                                                        </MDTypography>

                                                                        <MDTypography
                                                                            variant="caption"
                                                                            fontWeight="medium"
                                                                            textAlign="right"
                                                                        >
                                                                            {formatSummaryMoney(
                                                                                carrierBuyingAmount,
                                                                                buyingCurrency
                                                                            )}
                                                                        </MDTypography>
                                                                    </Box>

                                                                    {/* Extra Costs: Original */}
                                                                    {extraBuyingRows.map(
                                                                        (item) => (
                                                                            <Box
                                                                                key={
                                                                                    item.key
                                                                                }
                                                                                display="flex"
                                                                                justifyContent="space-between"
                                                                                alignItems="center"
                                                                                gap={2}
                                                                                mb={0.75}
                                                                            >
                                                                                <MDTypography variant="caption">
                                                                                    {
                                                                                        item.label
                                                                                    }
                                                                                </MDTypography>

                                                                                <MDTypography
                                                                                    variant="caption"
                                                                                    textAlign="right"
                                                                                >
                                                                                    {formatSummaryMoney(
                                                                                        item.originalAmount,
                                                                                        item.originalCurrency
                                                                                    )}
                                                                                </MDTypography>
                                                                            </Box>
                                                                        )
                                                                    )}

                                                                    {serviceBuyingRows.map((item) => (
                                                                        <Box
                                                                            key={item.key}
                                                                            display="flex"
                                                                            justifyContent="space-between"
                                                                            alignItems="center"
                                                                            gap={2}
                                                                            mb={0.75}
                                                                        >
                                                                            <MDTypography variant="caption">
                                                                                {item.label}
                                                                            </MDTypography>
                                                                            <MDTypography variant="caption" textAlign="right">
                                                                                {formatSummaryMoney(
                                                                                    item.originalAmount,
                                                                                    item.originalCurrency
                                                                                )}
                                                                            </MDTypography>
                                                                        </Box>
                                                                    ))}

                                                                    {extraBuyingRows.length ===
                                                                        0 && serviceBuyingRows.length === 0 && (
                                                                            <MDTypography
                                                                                variant="caption"
                                                                                color="text"
                                                                                sx={{
                                                                                    opacity: 0.55,
                                                                                }}
                                                                            >
                                                                                No additional
                                                                                buying costs
                                                                            </MDTypography>
                                                                        )}
                                                                </MDBox>
                                                            </Grid>

                                                            {/* SELLING CARD */}
                                                            <Grid
                                                                item
                                                                xs={12}
                                                                md={6}
                                                            >
                                                                <MDBox
                                                                    sx={{
                                                                        height: "100%",
                                                                        p: 1.5,
                                                                        borderRadius: 1.5,
                                                                        backgroundColor:
                                                                            "#f0fdf4",
                                                                        border:
                                                                            "1px solid #bbf7d0",
                                                                    }}
                                                                >
                                                                    <Box
                                                                        display="flex"
                                                                        justifyContent="space-between"
                                                                        alignItems="flex-start"
                                                                        gap={2}
                                                                        pb={1}
                                                                        mb={1}
                                                                        sx={{
                                                                            borderBottom:
                                                                                "1px solid #bbf7d0",
                                                                        }}
                                                                    >
                                                                        <Box>
                                                                            <MDTypography
                                                                                variant="caption"
                                                                                fontWeight="bold"
                                                                                display="block"
                                                                            >
                                                                                Selling
                                                                                Total
                                                                            </MDTypography>

                                                                            <MDTypography
                                                                                variant="caption"
                                                                                color="text"
                                                                                sx={{
                                                                                    opacity: 0.6,
                                                                                }}
                                                                            >
                                                                                {`Converted to ${sellingCurrency}`}
                                                                            </MDTypography>
                                                                        </Box>

                                                                        <MDTypography
                                                                            variant="caption"
                                                                            fontWeight="bold"
                                                                            textAlign="right"
                                                                            sx={{
                                                                                color:
                                                                                    finalSelling ===
                                                                                    null
                                                                                        ? "#d32f2f"
                                                                                        : "#15803d",
                                                                            }}
                                                                        >
                                                                            {formatSummaryMoney(
                                                                                finalSelling,
                                                                                sellingCurrency
                                                                            )}
                                                                        </MDTypography>
                                                                    </Box>

                                                                    <>
                                                                        <Box
                                                                            display="flex"
                                                                            justifyContent="space-between"
                                                                            alignItems="center"
                                                                            gap={2}
                                                                            mb={0.75}
                                                                        >
                                                                            <MDTypography variant="caption">
                                                                                {isBreakdown
                                                                                    ? "Carrier Selling"
                                                                                    : "All-in Ocean Selling"}
                                                                            </MDTypography>

                                                                            <MDTypography
                                                                                variant="caption"
                                                                                fontWeight="medium"
                                                                                textAlign="right"
                                                                            >
                                                                                {formatSummaryMoney(
                                                                                    mainSellingAmount,
                                                                                    sellingCurrency
                                                                                )}
                                                                            </MDTypography>
                                                                        </Box>

                                                                        {extraSellingRows.map((item) => (
                                                                            <Box
                                                                                key={item.key}
                                                                                display="flex"
                                                                                justifyContent="space-between"
                                                                                alignItems="center"
                                                                                gap={2}
                                                                                mb={0.75}
                                                                            >
                                                                                <MDTypography variant="caption">
                                                                                    {item.label}
                                                                                </MDTypography>
                                                                                <MDTypography
                                                                                    variant="caption"
                                                                                    textAlign="right"
                                                                                >
                                                                                    {formatSummaryMoney(
                                                                                        item.originalAmount,
                                                                                        item.originalCurrency
                                                                                    )}
                                                                                </MDTypography>
                                                                            </Box>
                                                                        ))}

                                                                        {serviceSellingRows.map((item) => (
                                                                            <Box
                                                                                key={item.key}
                                                                                display="flex"
                                                                                justifyContent="space-between"
                                                                                alignItems="center"
                                                                                gap={2}
                                                                                mb={0.75}
                                                                            >
                                                                                <MDTypography variant="caption">
                                                                                    {item.label}
                                                                                </MDTypography>
                                                                                <MDTypography
                                                                                    variant="caption"
                                                                                    textAlign="right"
                                                                                    fontWeight={item.included ? "bold" : "regular"}
                                                                                >
                                                                                    {item.included
                                                                                        ? "Included"
                                                                                        : formatSummaryMoney(
                                                                                            item.originalAmount,
                                                                                            item.originalCurrency
                                                                                        )}
                                                                                </MDTypography>
                                                                            </Box>
                                                                        ))}

                                                                        {extraSellingRows.length === 0 &&
                                                                            serviceSellingRows.length === 0 &&
                                                                            mainSellingAmount === 0 && (
                                                                                <MDTypography
                                                                                    variant="caption"
                                                                                    color="text"
                                                                                    sx={{ opacity: 0.55 }}
                                                                                >
                                                                                    No selling items
                                                                                </MDTypography>
                                                                            )}
                                                                    </>
                                                                </MDBox>
                                                            </Grid>
                                                        </Grid>
                                                    </MDBox>
                                                );
                                            }
                                        )}
                                    </MDBox>
                                    <MDBox mt={3}>
                                        <Grid container spacing={3}>
                                            <Grid item xs={12}>
                                                <TextField
                                                    type="text"
                                                    label="Description"
                                                    value={description}
                                                    onChange={(e) => setDescription(e.target.value)}
                                                    fullWidth
                                                    variant="outlined"
                                                    InputLabelProps={{ shrink: true }}
                                                    sx={{ "& .MuiInputBase-root": { height: 40 } }}
                                                />
                                            </Grid>
                                        </Grid>
                                    </MDBox>
                                    <MDBox mt={3}>
                                        <Grid container spacing={3}>
                                            <Grid item xs={12} sm={3}>
                                                <Autocomplete
                                                    options={statusOptions}
                                                    getOptionLabel={(o) => o.label}
                                                    value={statusOptions.find((o) => o.value === selectedStatus || o.label === selectedStatus) || null}
                                                    onChange={(e, newValue) => setSelectedStatus(newValue?.value || newValue?.label || "")}
                                                    isOptionEqualToValue={(opt, val) => opt?.value === val?.value}
                                                    renderOption={({ key: optionKey, ...props }, option) => (
                                                        <li key={optionKey} {...props} style={{ display: "flex", alignItems: "center" }}>
                                                            <Dot color={option.color} />
                                                            {option.label}
                                                        </li>
                                                    )}
                                                    renderInput={(params) => {
                                                        const current =
                                                            statusOptions.find((o) => o.value === selectedStatus || o.label === selectedStatus) || null;

                                                        return (
                                                            <TextField
                                                                {...params}
                                                                label="Select Business Status"
                                                                variant="outlined"
                                                                InputLabelProps={{ shrink: true }}
                                                                sx={{
                                                                    ...inputStyle,
                                                                    "& .MuiInputBase-root": { alignItems: "center" }, // ✅ input satırını ortala
                                                                }}
                                                                InputProps={{
                                                                    ...params.InputProps,
                                                                    startAdornment: (
                                                                        <span style={{ display: "flex", alignItems: "center", marginRight: 6 }}>
                                                                            <Dot color={current?.color} />
                                                                            {params.InputProps.startAdornment}
                                                                        </span>
                                                                    ),
                                                                }}
                                                            />
                                                        );
                                                    }}

                                                />

                                            </Grid>
                                        </Grid>
                                    </MDBox>
                                </MDBox>
                            </Grid>
                        </Grid>
                    </MDBox>
                    <div>
                        <form onSubmit={submitHandler}>
                            <Button
                                variant="contained"
                                onClick={submitHandler}
                                disabled={loading || !!successMessage}
                                style={{ backgroundColor: "#0B1F3A", color: "#FFFFFF" }}
                            >
                                {isEditing ? "Update Ocean Quote" : "Create Ocean Quote"}
                            </Button>
                        </form>
                    </div>
                </MDBox>
            </Grid>
            <Dialog
                open={customerModalOpen}
                onClose={() => setCustomerModalOpen(false)}
                maxWidth="xl"
                fullWidth
            >
                <DialogTitle>
                    Create Customer

                    <IconButton
                        onClick={() => setCustomerModalOpen(false)}
                        sx={{
                            position: "absolute",
                            right: 12,
                            top: 8,
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent dividers>
                    <CreateCustomer
                        isModal
                        onClose={() => setCustomerModalOpen(false)}
                        onCreated={handleCustomerCreatedFromModal}
                    />
                </DialogContent>
            </Dialog>
        </Grid>
    );
};

export default OceanForm;